// app/javascript/controllers/radioplayer_controller.js
import { Controller } from "@hotwired/stimulus"
import ScreenWakeLock from "../helpers/screen_wake_lock"
import LongplayLanguage from "../helpers/longplay_language"

const JUICEBOX_LEGACY_PLAYLIST_KEY = "fm313.juicebox.playlist.v1"
const JUICEBOX_PLAYLIST_KEY = "fm313.juicebox.playlists.v2"
const JUICEBOX_PLAYLIST_VERSION = 2
const JUICEBOX_PLAYLIST_LIMIT = 60
const JUICEBOX_PLAYLIST_LIBRARY_LIMIT = 12
const JUICEBOX_PLAYLIST_NAME_LIMIT = 40
const JUICEBOX_PLAYLIST_MAX_BYTES = 32768
const JUICEBOX_LEGACY_PLAYLIST_MAX_BYTES = 8192
const JUICEBOX_DEFAULT_PLAYLIST_ID = "p_default"
const JUICEBOX_DEFAULT_PLAYLIST_NAME = "my playlist"
const JUICEBOX_SWIPE_INTENT_PX = 10
const JUICEBOX_SWIPE_VERTICAL_CANCEL_PX = 12
const JUICEBOX_SWIPE_AMBIGUOUS_CANCEL_PX = 28
const JUICEBOX_SWIPE_POINTER_TYPES = new Set(["touch", "pen"])
const LONGPLAY_SYNC_INTERVAL_MS = 5000
const LONGPLAY_PROGRAM_POLL_MS = 5000
const LONGPLAY_HARD_DRIFT_SECONDS = 2.5
const LONGPLAY_SOFT_DRIFT_SECONDS = 0.35
const LONGPLAY_SLOW_RATE = 0.985
const LONGPLAY_FAST_RATE = 1.015
const LONGPLAY_WHOLE_TRACK_START_GRACE_SECONDS = 8
const LONGPLAY_WHOLE_TRACK_MAX_CATCHUP_RATE = 1.06
const LONGPLAY_WHOLE_TRACK_SYNCED_DRIFT_SECONDS = 0.12
const LONGPLAY_SPEECH_START_GRACE_SECONDS = 2
const LONGPLAY_SPEECH_FINISH_GRACE_SECONDS = 1.5
const LONGPLAY_BOUNDARY_GRACE_MS = 125
const LONGPLAY_RELOAD_PAUSE_KEY = "fm313.longplay.reload-paused.v1"
const LONGPLAY_DJ_MIX_MODE = "duck_next_track_restart_v1"
const LONGPLAY_DJ_BED_HARD_DRIFT_SECONDS = 0.5
// Release tooling verifies this exact marker in both the esbuild output and
// the digested public asset. That makes a stale pre-shared-clock bundle a
// deployment failure instead of silently starting every listener at track 1.
const LONGPLAY_LIVE_CLOCK_ASSET_MARKER = "fm313-longplay-live-clock-v4-dj-mixer"

export default class extends Controller {
  static targets = [
    "audio", "bedAudio", "artist", "lyrics",
    "nextBtn", "prevBtn",
    "repeatBtn", "repeatBadge",
    "album", "albumCover", "albumLink",
    "visualWrap", "artGrid",
    "title",
    "titlePlayCount", "rowPlayCount",
    "shareBtn", "muteBtn", "originalLink",
    "seekBar", "waveProgress", "currentTime", "durationTime",
    "trackToggleBtn", "trackToggleIcon",
    "transportToggleBtn", "transportToggleIcon",
    "playlistOverlay", "playlistList", "playlistEmpty",
    "playlistStatus", "playlistNotice", "playlistCount",
    "playlistPicker", "playlistOpenLabel", "playlistName",
    "playlistActiveSummary", "playlistForm", "playlistFormLabel",
    "playlistFormInput", "playlistFormSubmit", "playlistFormError",
    "playlistDelete", "tapOverlay", "languageSelect", "languageStatus"
  ]

  static values = {
    playlist: Array,
    ads: Array,                 // from data-radioplayer-ads-value
    playBaseUrl: String,
    stationMode: { type: String, default: "playlist" },
    stationEpoch: { type: Number, default: 0 },
    stationServerTime: { type: Number, default: 0 },
    stationProgramVersion: String,
    stationVersionUrl: String,
    languagesUrl: String,

    // Optional: URL for Thing.cover_art video (if video) to loop in the background (muted)
    bgVideoUrl: String,

    // How often to inject an ad (seconds of actual listening time)
    adIntervalSeconds: { type: Number, default: 1200 }, // 20 minutes

    // How long NEXT/PREV are locked at the start of an ad
    adSkipLockSeconds: { type: Number, default: 10 },

    // Run an ad as soon as player starts if ads are available
    preRollEnabled: { type: Boolean, default: true }
  }

  connect() {
    this._connected = true
    // music state
    this.index = 0
    this.stationProgram = Array.isArray(this.playlistValue) ? this.playlistValue : []
    this.stationPlaylist = this.stationProgram.filter(track => !track?.is_dj_segment)
    this.playbackQueue = this.stationProgram
    this.queueSource = "station"
    this.liveStation = this.stationModeValue === "live"
    let languageStorage = null
    try { languageStorage = window.localStorage } catch (_) {}
    this.longplayLanguage = new LongplayLanguage({ storage: languageStorage, origin: window.location.origin })
    this._languageAbort = null
    this._languageTimer = null
    if (this.hasLanguageSelectTarget) this.languageSelectTarget.value = this.longplayLanguage.preferred
    if (this.liveStation) this.element.dataset.longplayLiveClockAsset = LONGPLAY_LIVE_CLOCK_ASSET_MARKER
    this._liveServerOffsetMs = this.liveStation && this.stationServerTimeValue > 0
      ? this.stationServerTimeValue - Date.now()
      : 0
    this._liveSyncTimer = null
    this._liveVersionTimer = null
    this._liveBoundaryTimer = null
    this._liveBoundarySignature = null
    this._liveVersionAbort = null
    this._liveVersionFetchActive = false
    this._liveProgramRefreshStarted = false
    this._liveVersionUrl = null
    this._liveLoadedVersion = null
    this._liveUserPaused = this.liveStation && this._consumeLiveReloadPauseState()
    this._livePendingHardSync = false
    this._liveWholeTrackIndex = null
    this._liveWholeTrackStartApplied = false
    this._livePrefetchAudio = null
    this._livePrefetchUrl = null
    this._livePlaybackIntent = false
    this._liveDjBedVoiceIndex = null
    this._liveDjBedTrackIndex = null
    this._liveDjBedUrl = null
    this._liveEndedIndex = null
    this._liveEndedTimer = null
    this._transientQueueTrackId = null
    this.repeatOne = false
    this._reportedPlayForCurrent = false
    this._playThreshold = 10
    this._primaryVideoEl = null // when Extra.cover_art is a video

    // ad state
    this._isAdMode = false
    this._preRoll = false
    this._adIndex = 0
    this._currentAd = null
    this._adVideoEl = null
    this._adLockUntil = 0

    // accumulated music listening time
    this._totalPlaySeconds = 0
    this._lastTimeUpdateTs = null
    this._adPending = false

    // listen-timer gate (true only while playback is happening)
    this._isListeningClockOn = false
    this._activeListenMedia = null

    // autoplay block tracking
    this._autoplayBlocked = false

    // Device-local, cross-station Juicebox playlist library. The savedPlaylist*
    // arrays remain the active list's working set so the player stays simple.
    this._playlistStorageWarning = null
    this._playlistLibraryNeedsPersist = false
    this.playlistLibrary = this._readPlaylistLibrary()
    this.activePlaylistId = this.playlistLibrary.activeId
    this.savedPlaylistIds = [...(this._activePlaylistRecord()?.ids || [])]
    this.savedPlaylist = []
    this._savedQueuePlaylistId = null
    this._playlistHydrationSequence = 0
    this._playlistHydrationAbort = null
    this._playlistSwipe = null
    this._suppressTrackClickId = null
    this._playlistReturnFocus = null
    this._draggedPlaylistId = null
    this._playlistNoticeTimer = null
    this._playlistFormMode = null

    this.screenWakeLock = new ScreenWakeLock({ element: this.element })

    if (this.hasAudioTarget) this.audioTarget.crossOrigin = "anonymous"
    if (this.hasBedAudioTarget) {
      this.bedAudioTarget.crossOrigin = "anonymous"
      this.bedAudioTarget.volume = 0.20
    }

    this._eventAbortController = new AbortController()
    this._bindEvents()
    this._syncRepeatOneState()
    if (this._playlistLibraryNeedsPersist) this._persistPlaylistLibrary()
    this._renderSavedPlaylist()
    this._hydrateSavedPlaylist()
    if (this._playlistStorageWarning) this._announcePlaylist(this._playlistStorageWarning)
    if (!this.liveStation) this._syncIndexFromUrl()
    this._mountBackgroundVideoIfAny()

    // load mute pref
    this._applyInitialMutePreference()

    // pre-roll if ads exist
    if (this.liveStation) {
      this._startLanguagePolling()
      this._startLiveStation()
    } else if (this._adsEnabled() && this.preRollEnabledValue) {
      this._preRoll = true
      this._playNextAd()
    } else {
      this._loadCurrent(true)
    }

    this._onPopState = () => {
      if (this.liveStation) return
      if (this._isAdMode) return // ignore URL changes during an ad
      if (this._syncIndexFromUrl()) this._loadCurrent(false)
    }
    window.addEventListener("popstate", this._onPopState)

    this._onPlaylistStorage = (event) => {
      if (this.liveStation) return
      if (event.key !== JUICEBOX_PLAYLIST_KEY) return
      this._reloadPlaylistLibraryFromStorage()
    }
    this._onPlaylistBlur = () => this._cancelPlaylistSwipe()
    this._onPlaylistVisibility = () => {
      if (document.hidden) this._cancelPlaylistSwipe()
      else if (this.liveStation) {
        this._syncLiveStation({ autoplay: !this._liveUserPaused, hard: true })
        this._pollLiveProgramVersion(this._liveVersionUrl, this._liveLoadedVersion)
      }
    }
    this._onLiveFocus = () => {
      if (this.liveStation) {
        this._syncLiveStation({ autoplay: !this._liveUserPaused, hard: true })
        this._pollLiveProgramVersion(this._liveVersionUrl, this._liveLoadedVersion)
      }
    }
    window.addEventListener("storage", this._onPlaylistStorage)
    window.addEventListener("blur", this._onPlaylistBlur)
    document.addEventListener("visibilitychange", this._onPlaylistVisibility)
    window.addEventListener("focus", this._onLiveFocus)

    console.debug("🎧 radioplayer connected")
  }

  disconnect() {
    this._connected = false
    this._languageAbort?.abort()
    if (this._languageTimer) window.clearInterval(this._languageTimer)
    this.screenWakeLock?.disconnect()
    this.screenWakeLock = null
    if (this._onPopState) window.removeEventListener("popstate", this._onPopState)
    if (this._onPlaylistStorage) window.removeEventListener("storage", this._onPlaylistStorage)
    if (this._onPlaylistBlur) window.removeEventListener("blur", this._onPlaylistBlur)
    if (this._onPlaylistVisibility) document.removeEventListener("visibilitychange", this._onPlaylistVisibility)
    if (this._onLiveFocus) window.removeEventListener("focus", this._onLiveFocus)
    this._onPopState = null
    this._onPlaylistStorage = null
    this._onPlaylistBlur = null
    this._onPlaylistVisibility = null
    this._onLiveFocus = null
    if (this._liveSyncTimer) window.clearInterval(this._liveSyncTimer)
    this._liveSyncTimer = null
    if (this._liveVersionTimer) window.clearInterval(this._liveVersionTimer)
    this._liveVersionTimer = null
    if (this._liveBoundaryTimer) window.clearTimeout(this._liveBoundaryTimer)
    this._liveBoundaryTimer = null
    this._liveBoundarySignature = null
    this._clearLiveEndedBoundary()
    this._clearLivePrefetch()
    this._stopLiveDjBed()
    this._liveVersionAbort?.abort?.()
    this._liveVersionAbort = null
    this._cancelPlaylistSwipe()
    this._cancelPlaylistHydration()
    this._eventAbortController?.abort()
    this._eventAbortController = null
    if (this._playlistNoticeTimer) window.clearTimeout(this._playlistNoticeTimer)
    this._teardownPrimaryVideo()
    this._removeAdPlayOverlay()
    const media = this._activeMedia()
    if (media) media.playbackRate = 1
    if (this.liveStation) this._clearLiveMediaSession()
  }

  // ===== Public actions =====

  next() {
    if (this.liveStation) return
    this._handleNextClick()
  }

  prev() {
    if (this.liveStation) return
    if (this._isAdMode) return // no prev during ad
    this._advance(-1)
  }

  // playlist row click
  jump(event) {
    event?.preventDefault?.()
    if (this.liveStation) return
    if (this._isAdMode) return
    const li = event.currentTarget.closest("[data-extra-id]")
    if (!li) return
    const id = this._normalizeTrackId(li.getAttribute("data-extra-id"))
    if (id && this._suppressTrackClickId === id) {
      this._suppressTrackClickId = null
      return
    }

    this.playbackQueue = this.stationProgram
    this.queueSource = "station"
    this._savedQueuePlaylistId = null
    this._transientQueueTrackId = null
    const i = this.stationProgram.findIndex(t => this._normalizeTrackId(t.id) === id)
    if (i >= 0) {
      this.index = i
      this._loadCurrent(true)
    }
  }

  startPlaylistSwipe(event) {
    if (this.liveStation) return
    if (event.isPrimary === false) return
    // Swipe is a touch/pen affordance. Capturing a desktop mouse pointer on
    // pointerdown retargets pointerup/click from the song link to this parent
    // surface in Chromium, so the link's `jump` action never fires.
    if (!JUICEBOX_SWIPE_POINTER_TYPES.has(event.pointerType)) return
    if (event.target instanceof Element && event.target.closest("[data-playlist-toggle]")) return
    if (event.clientX < 18 || event.clientX > window.innerWidth - 18) return

    const row = event.currentTarget.closest("[data-extra-id]")
    const id = this._normalizeTrackId(row?.dataset.extraId)
    const link = row?.querySelector(".playlist-btn")
    if (!row || !link || !id) return

    this._cancelPlaylistSwipe()
    this._playlistSwipe = {
      id,
      row,
      link,
      surface: event.currentTarget,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      distanceX: 0,
      distanceY: 0,
      horizontal: false,
      threshold: this._playlistSwipeThreshold(event.currentTarget)
    }
    // Capture immediately so a quick swipe that leaves the row still gets its
    // move/up events. touch-action: pan-y keeps ordinary vertical page scroll.
    try { event.currentTarget.setPointerCapture(event.pointerId) } catch (_) {}
  }

  movePlaylistSwipe(event) {
    const swipe = this._playlistSwipe
    if (!swipe || swipe.pointerId !== event.pointerId) return

    const distanceX = event.clientX - swipe.startX
    const distanceY = event.clientY - swipe.startY
    const absX = Math.abs(distanceX)
    const absY = Math.abs(distanceY)

    if (!swipe.horizontal) {
      if (absY >= JUICEBOX_SWIPE_VERTICAL_CANCEL_PX && absY > absX * 1.15) {
        this._cancelPlaylistSwipe()
        return
      }
      if (absX < JUICEBOX_SWIPE_INTENT_PX) return

      if (absX <= absY * 1.05) {
        if (Math.hypot(absX, absY) >= JUICEBOX_SWIPE_AMBIGUOUS_CANCEL_PX) {
          this._cancelPlaylistSwipe()
        }
        return
      }

      swipe.horizontal = true
      swipe.row.classList.add("is-swiping")
    }

    event.preventDefault()
    const maxDistance = swipe.threshold + 28
    swipe.distanceX = Math.max(-maxDistance, Math.min(distanceX, maxDistance))
    swipe.distanceY = distanceY
    swipe.link.style.setProperty("--juicebox-swipe-x", `${swipe.distanceX}px`)
    const action = swipe.distanceX >= 0 ? "add" : "remove"
    const canCommit = this._playlistSwipeCanCommit(swipe.id, action)
    swipe.row.classList.toggle("is-swipe-right", action === "add")
    swipe.row.classList.toggle("is-swipe-left", action === "remove")
    swipe.row.classList.toggle("is-swipe-ready", Math.abs(swipe.distanceX) >= swipe.threshold && canCommit)
  }

  endPlaylistSwipe(event) {
    const swipe = this._playlistSwipe
    if (!swipe || swipe.pointerId !== event.pointerId) return

    const action = swipe.distanceX >= 0 ? "add" : "remove"
    const shouldCommit = swipe.horizontal &&
      Math.abs(swipe.distanceX) >= swipe.threshold &&
      this._playlistSwipeCanCommit(swipe.id, action)
    this._finishPlaylistSwipe(swipe, { action: shouldCommit ? action : null })
    if (swipe.horizontal) this._suppressPlaylistTrackClick(swipe.id)
    if (!shouldCommit) return
    if (action === "add") this._addTrackToSavedPlaylist(swipe.id)
    else this._removeTrackFromSavedPlaylist(swipe.id)
  }

  cancelPlaylistSwipe() {
    const swipe = this._playlistSwipe
    this._cancelPlaylistSwipe()
    if (swipe?.horizontal) this._suppressPlaylistTrackClick(swipe.id)
  }

  toggleTrackInPlaylist(event) {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    if (this.liveStation) return
    const row = event.currentTarget.closest("[data-extra-id]")
    const id = this._normalizeTrackId(row?.dataset.extraId)
    if (!id) return
    if (this.savedPlaylistIds.includes(id)) this._removeTrackFromSavedPlaylist(id)
    else this._addTrackToSavedPlaylist(id)
  }

  switchPlaylist(event) {
    const id = this._normalizePlaylistId(event?.currentTarget?.value)
    if (!id || id === this.activePlaylistId) return
    this._activatePlaylist(id, { announce: true })
  }

  openNewPlaylistForm(event) {
    event?.preventDefault?.()
    this._openPlaylistForm("new", event?.currentTarget)
  }

  openCopyPlaylistForm(event) {
    event?.preventDefault?.()
    this._openPlaylistForm("copy", event?.currentTarget)
  }

  openRenamePlaylistForm(event) {
    event?.preventDefault?.()
    this._openPlaylistForm("rename", event?.currentTarget)
  }

  cancelPlaylistForm(event) {
    event?.preventDefault?.()
    this._closePlaylistForm({ restoreFocus: true })
  }

  playlistFormKeydown(event) {
    if (event.key !== "Escape") return
    event.preventDefault()
    event.stopPropagation()
    this._closePlaylistForm({ restoreFocus: true })
  }

  submitPlaylistForm(event) {
    event?.preventDefault?.()
    if (!this._playlistFormMode || !this.hasPlaylistFormInputTarget) return

    const mode = this._playlistFormMode
    const name = this._normalizePlaylistName(this.playlistFormInputTarget.value)
    const excludingId = mode === "rename" ? this.activePlaylistId : null
    if (!name) return this._showPlaylistFormError("enter a playlist name")
    if (this._playlistNameExists(name, excludingId)) {
      return this._showPlaylistFormError("that playlist name is already in use")
    }

    if (mode === "rename") {
      const playlist = this._activePlaylistRecord()
      if (!playlist) return
      const oldName = playlist.name
      const oldUpdatedAt = playlist.updatedAt
      playlist.name = name
      playlist.updatedAt = Date.now()
      if (!this._persistPlaylistLibrary()) {
        playlist.name = oldName
        playlist.updatedAt = oldUpdatedAt
        this._renderSavedPlaylist()
        return
      }
      this._renderSavedPlaylist()
      this._announcePlaylist(`${oldName} renamed ${name}`)
    } else {
      const ids = mode === "copy" ? [...this.savedPlaylistIds] : []
      const tracks = mode === "copy" ? [...this.savedPlaylist] : []
      if (!this._createPlaylist(name, ids, tracks)) return
      this._announcePlaylist(mode === "copy" ? `${name} saved as a copy` : `${name} created`)
    }

    this._closePlaylistForm({ restoreFocus: false })
    if (this.hasPlaylistPickerTarget) this.playlistPickerTarget.focus()
  }

  deletePlaylist(event) {
    event?.preventDefault?.()
    const playlists = this.playlistLibrary?.playlists || []
    const active = this._activePlaylistRecord()
    if (!active || playlists.length <= 1) {
      this._announcePlaylist("keep one playlist; clear its songs instead")
      return
    }
    if (!window.confirm(`Delete “${active.name}”? This removes only this device’s playlist; songs remain on FM.`)) return

    const deletingPlayingPlaylist = this.queueSource === "saved" && this._savedQueuePlaylistId === active.id
    const current = deletingPlayingPlaylist ? this._currentExtra() : null
    const previousPlaylists = playlists
    const previousActiveId = this.activePlaylistId
    const previousIds = [...this.savedPlaylistIds]
    const previousTracks = [...this.savedPlaylist]
    this._cancelPlaylistSwipe()
    this._draggedPlaylistId = null
    this._cancelPlaylistHydration()
    const deletedIndex = playlists.findIndex(playlist => playlist.id === active.id)
    this.playlistLibrary.playlists = playlists.filter(playlist => playlist.id !== active.id)
    const fallback = this.playlistLibrary.playlists[Math.min(deletedIndex, this.playlistLibrary.playlists.length - 1)]
    this.activePlaylistId = fallback.id
    this.playlistLibrary.activeId = fallback.id
    this.savedPlaylistIds = [...fallback.ids]
    this.savedPlaylist = []
    if (!this._persistPlaylistLibrary()) {
      this.playlistLibrary.playlists = previousPlaylists
      this.playlistLibrary.activeId = previousActiveId
      this.activePlaylistId = previousActiveId
      this.savedPlaylistIds = previousIds
      this.savedPlaylist = previousTracks
      this._renderSavedPlaylist()
      if (previousTracks.length !== previousIds.length) this._hydrateSavedPlaylist()
      return
    }
    if (deletingPlayingPlaylist) this._switchToStationQueue(current)
    const manage = this.element.querySelector(".juicebox-playlist-manage")
    if (manage) manage.open = false
    this._closePlaylistForm({ restoreFocus: false })
    this._renderSavedPlaylist()
    this._hydrateSavedPlaylist()
    this._announcePlaylist(`${active.name} deleted; ${fallback.name} selected`)
    if (this.hasPlaylistPickerTarget) this.playlistPickerTarget.focus()
  }

  openPlaylist(event) {
    event?.preventDefault?.()
    if (!this.hasPlaylistOverlayTarget) return
    this._playlistReturnFocus = event?.currentTarget || document.activeElement
    this._renderSavedPlaylist()
    if (typeof this.playlistOverlayTarget.showModal === "function") {
      if (!this.playlistOverlayTarget.open) this.playlistOverlayTarget.showModal()
    } else {
      this.playlistOverlayTarget.setAttribute("open", "")
    }
  }

  closePlaylist(event) {
    event?.preventDefault?.()
    if (!this.hasPlaylistOverlayTarget) return
    if (typeof this.playlistOverlayTarget.close === "function") {
      if (this.playlistOverlayTarget.open) this.playlistOverlayTarget.close()
    } else {
      this.playlistOverlayTarget.removeAttribute("open")
      this.playlistClosed()
    }
  }

  playlistClosed() {
    this._closePlaylistForm({ restoreFocus: false })
    this._playlistReturnFocus?.focus?.()
    this._playlistReturnFocus = null
  }

  closePlaylistFromBackdrop(event) {
    if (event.target === this.playlistOverlayTarget) this.closePlaylist(event)
  }

  clearPlaylist(event) {
    event?.preventDefault?.()
    if (!this.savedPlaylistIds.length) return
    const name = this._activePlaylistRecord()?.name || JUICEBOX_DEFAULT_PLAYLIST_NAME
    if (!window.confirm(`Clear every song from “${name}”? The named playlist will remain.`)) return

    const current = this._currentExtra()
    const previousIds = [...this.savedPlaylistIds]
    const previousTracks = [...this.savedPlaylist]
    this.savedPlaylistIds = []
    this.savedPlaylist = []
    if (!this._persistSavedPlaylistIds()) {
      this.savedPlaylistIds = previousIds
      this.savedPlaylist = previousTracks
      this._renderSavedPlaylist()
      return
    }
    const manage = event?.currentTarget?.closest?.(".juicebox-playlist-manage")
    if (manage) manage.open = false
    if (this.queueSource === "saved" && this._savedQueuePlaylistId === this.activePlaylistId) {
      this._switchToStationQueue(current)
    }
    this._renderSavedPlaylist()
    this._announcePlaylist(`${name} cleared`)
  }

  toggleRepeatOne(event) {
    event?.preventDefault?.()
    if (this.liveStation) return
    this.repeatOne = !this.repeatOne
    this._syncRepeatOneState({ applyToMedia: !this._isAdMode })
    const suffix = this._isAdMode ? " after this ad" : ""
    this._announcePlaylist(`repeat one ${this.repeatOne ? "on" : "off"}${suffix}`)
  }

  _syncRepeatOneState({ applyToMedia = true } = {}) {
    if (applyToMedia) {
      if (this.hasAudioTarget) this.audioTarget.loop = this.repeatOne
      if (this._primaryVideoEl) this._primaryVideoEl.loop = this.repeatOne
    }

    if (this.hasRepeatBtnTarget) {
      this.repeatBtnTarget.classList.toggle("is-on", this.repeatOne)
      this.repeatBtnTarget.setAttribute("aria-pressed", this.repeatOne ? "true" : "false")
      this.repeatBtnTarget.setAttribute(
        "aria-label",
        this.repeatOne ? "Turn repeat one off" : "Repeat the current track"
      )
      this.repeatBtnTarget.title = this.repeatOne ? "Repeat one is on" : "Repeat one is off"
      this.repeatBtnTarget.textContent = "REPEAT 1"
    }
    if (this.hasRepeatBadgeTarget) {
      this.repeatBadgeTarget.style.display = this.repeatOne ? "inline-block" : "none"
    }
  }

  _restartRepeatedMedia(media) {
    if (!media) return
    try { media.currentTime = 0 } catch (_) {}
    this._updateProgressUI(media)
    this._safePlay(media, "repeat-one")
  }

  toggleTrackPlayback(event) {
    event?.preventDefault?.()
    if (this._isAdMode) return

    const media = this._activeMedia()
    if (!media) return

    if (media.paused) {
      if (this.liveStation) {
        this._liveUserPaused = false
        this._livePlaybackIntent = true
        this._syncLiveStation({ autoplay: false, hard: true })
      }
      this._safePlay(media, "overlay-toggle")
      if (this.liveStation) this._resumeLiveDjBed()
    } else {
      if (this.liveStation) this._liveUserPaused = true
      media.pause()
      if (this.liveStation) this._pauseLiveDjBed()
    }
    this._syncVisualVideoPlayback()
    this._refreshTrackToggleUI()
  }

  seekTo() {
    if (this.liveStation) return
    const media = this._activeMedia()
    if (!media || !this.hasSeekBarTarget) return
    const nextTime = Number(this.seekBarTarget.value || 0)
    if (!Number.isFinite(nextTime)) return
    try {
      media.currentTime = nextTime
      this._updateProgressUI(media)
    } catch (_) {}
  }

  // from data-action on mute button
  toggleMuteAll() {
    const wantMuted = !(this.audioTarget?.muted)
    this._applyMute(wantMuted, { fromUser: true })
  }

  // Called by ad overlay play button (user gesture)
  userStart() {
    this._autoplayBlocked = false
    if (this.liveStation) {
      this._liveUserPaused = false
      this._livePlaybackIntent = true
      this._syncLiveStation({ autoplay: false, hard: true })
    }
    this._hideTapOverlay()
    this._removeAdPlayOverlay()
    this._tryResumePlayback()
  }

  userStartMuted() {
    this._applyMute(true, { fromUser: true })
    this.userStart()
  }

  // ===== Wiring & timers =====

  _bindEvents() {
    if (!this.hasAudioTarget) return
    const options = { signal: this._eventAbortController.signal }
    this.screenWakeLock?.bindMedia(this.audioTarget, options)

    this.audioTarget.addEventListener("ended", () => {
      if (this._isAdMode) {
        // ad finished
        this._endAdAndResume()
      } else if (this.liveStation) {
        this._handleLiveMediaEnded()
      } else if (this.repeatOne) {
        this._restartRepeatedMedia(this.audioTarget)
      } else {
        // music track finished
        this._maybeQueueAdAndNext()
      }
    }, options)

    this.audioTarget.addEventListener("loadedmetadata", () => {
      this._recomputeThreshold()
      if (this.liveStation) this._syncLiveStation({ autoplay: !this._liveUserPaused, hard: true })
    }, options)

    this.audioTarget.addEventListener("timeupdate", () => {
      if (!this.liveStation) this._maybeReportPlay()
      this._updateListenTimer()
      this._updateProgressUI(this.audioTarget)
      if (this.liveStation) this._correctLiveDrift(this.audioTarget)
    }, options)

    // only start the listening timer when playback truly begins
    this.audioTarget.addEventListener("playing", () => {
      if (!this._isAdMode) this._resumeListenTimer()
      if (!this._isAdMode) this._maybeReportDjOnAir()
      if (this.liveStation && !this._isAdMode) {
        this._livePlaybackIntent = true
        this._resumeLiveDjBed()
        this._primeNextLiveAudio()
      }
      this._hideTapOverlay()
      this._syncVisualVideoPlayback()
      this._refreshTrackToggleUI()
    }, options)

    // stop/pause counting during non-playing states
    this.audioTarget.addEventListener("pause", () => {
      this._pauseListenTimer()
      if (this.liveStation) this._pauseLiveDjBed()
      this._syncVisualVideoPlayback()
      this._refreshTrackToggleUI()
    }, options)
    this.audioTarget.addEventListener("ended", () => this._syncVisualVideoPlayback(), options)
    this.audioTarget.addEventListener("waiting", () => this._pauseListenTimer(), options) // buffering
    this.audioTarget.addEventListener("seeking", () => this._pauseListenTimer(), options)
    this.audioTarget.addEventListener("stalled", () => this._pauseListenTimer(), options)
    this.audioTarget.addEventListener("error", () => {
      this._pauseListenTimer()
      if (this.liveStation) this._handleLiveMediaEnded()
    }, options)
    this.audioTarget.addEventListener("suspend", () => this._pauseListenTimer(), options)
    this.audioTarget.addEventListener("emptied", () => this._pauseListenTimer(), options)
    this.audioTarget.addEventListener("loadedmetadata", () => this._updateProgressUI(this.audioTarget), options)
    this.audioTarget.addEventListener("ended", () => this._refreshTrackToggleUI(), options)

    if (this.hasBedAudioTarget) {
      this.bedAudioTarget.addEventListener("loadedmetadata", () => {
        const expected = this._liveProgramPosition()
        if (expected) this._syncLiveDjBed(expected, { hard: true })
      }, options)
      this.bedAudioTarget.addEventListener("error", () => this._stopLiveDjBed(), options)
    }

    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.addEventListener("click", e => {
        e.preventDefault()
        this._handleNextClick()
      }, options)
    }

    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.addEventListener("click", e => {
        e.preventDefault()
        if (!this._isAdMode) this._advance(-1)
      }, options)
    }
  }

  _recomputeThreshold() {
    const dur = Number(this.audioTarget.duration || 0)
    const pct = dur ? dur * 0.30 : 5
    this._playThreshold = Math.max(5, Math.min(25, pct))
  }

  _maybeReportPlay() {
    if (this.liveStation) return
    if (this._isAdMode) return
    if (this._reportedPlayForCurrent) return
    const t = this.audioTarget?.currentTime || 0
    if (t >= this._playThreshold) {
      this._reportedPlayForCurrent = true
      const extra = this._currentExtra()
      if (extra?.id && !extra?.is_dj_segment) this._reportPlay(extra.id)
    }
  }

  _maybeReportDjOnAir() {
    const current = this._currentExtra()
    const segmentId = String(current?.dj_segment_id || "")
    if (!current?.is_dj_segment || !/^lps_[0-9a-f]{24}$/.test(segmentId)) return
    if (this._reportedDjOnAirForCurrent) return

    this._reportedDjOnAirForCurrent = true
    const headers = { Accept: "application/json" }
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) headers["X-CSRF-Token"] = token
    fetch(`/__longplay/segments/${encodeURIComponent(segmentId)}/playing`, {
      method: "POST",
      headers,
      credentials: "include"
    }).catch(() => {})
  }

  _reportPlay(extraId) {
    const url = `/extras/${encodeURIComponent(extraId)}/play`
    const headers = { Accept: "application/json" }
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) headers["X-CSRF-Token"] = token

    fetch(url, { method: "POST", headers, credentials: "include" })
      .then(async r => (r.ok ? r.json().catch(() => ({})) : null))
      .then(json => {
        if (json && typeof json.plays_count === "number") {
          this._updatePlayCounters(extraId, json.plays_count)
        }
      })
      .catch(() => {})
  }

  // listen-timer for AUDIO
  _resumeListenTimer() {
    if (this._isAdMode) return
    this._isListeningClockOn = true
    this._lastTimeUpdateTs = performance.now()
    this._activeListenMedia = this.audioTarget
  }

  _pauseListenTimer() {
    this._isListeningClockOn = false
    this._lastTimeUpdateTs = null
    this._activeListenMedia = null
  }

  _updateListenTimer() {
    if (!this._adsEnabled()) {
      this._lastTimeUpdateTs = null
      return
    }

    // only count continuous playback of MUSIC (not ads)
    if (!this.audioTarget || this._isAdMode || !this._isListeningClockOn) {
      this._lastTimeUpdateTs = null
      return
    }

    const now = performance.now()
    if (this._lastTimeUpdateTs) {
      const delta = (now - this._lastTimeUpdateTs) / 1000
      if (delta > 0 && delta < 5) {
        this._totalPlaySeconds += delta
      }
    }
    this._lastTimeUpdateTs = now

    if (!this._adPending && this._totalPlaySeconds >= this.adIntervalSecondsValue) {
      // current track will be followed by an ad injection
      this._adPending = true
    }
  }

  // ===== UI helpers =====

  _updatePlayCounters(extraId, rawCount) {
    const formatted = this._formatCount(rawCount)
    if (this.hasTitlePlayCountTarget) {
      this.titlePlayCountTarget.textContent = formatted
    }
    if (this.hasRowPlayCountTarget) {
      this.rowPlayCountTargets.forEach(el => {
        if (String(el.dataset.extraId) === String(extraId)) {
          el.textContent = formatted
        }
      })
    }
    const cur = this._currentExtra()
    if (cur && String(cur.id) === String(extraId)) {
      cur.plays_count = rawCount
    }
  }

  _formatCount(n) {
    if (n < 1000) return String(n)
    if (n < 1_000_000) {
      return `${(n / 1000).toFixed(n % 1000 >= 100 ? 1 : 0)}k`
    }
    return `${(n / 1_000_000).toFixed(n % 1_000_000 >= 100_000 ? 1 : 0)}m`
  }

  _setTicker(title = "", artist = "", album = "") {
    const text = this._composeTickerText(title, artist, album)
    this._setDocTitle(title)

    if (!this.hasTitleTarget) return
    const span = this.titleTarget.querySelector(".title-ticker-text")
    if (!span) return

    if (span.textContent !== text) span.textContent = text

    // restart CSS marquee
    span.style.animation = "none"
    // eslint-disable-next-line no-unused-expressions
    span.offsetHeight
    span.style.animation = ""
    this.titleTarget.setAttribute("aria-label", text)
  }

  _composeTickerText(title /*, _artist, _album */) {
    const t = this._normalizeDisplayTitle(title)
    return t || " "
  }

  _setDocTitle(title) {
    const want = this._normalizeDisplayTitle(title) || document.title
    if (document.title !== want) document.title = want
  }

  _normalizeDisplayTitle(value) {
    return String(value || "")
      .replace(/[ _]+$/g, "")
      .replace(/\s+/g, " ")
      .trim()
  }

  _safeHttpUrl(value) {
    if (!value) return null
    try {
      const url = new URL(String(value), window.location.origin)
      return ["http:", "https:"].includes(url.protocol) ? url.toString() : null
    } catch (_) {
      return null
    }
  }

  // ===== Device-local Juicebox playlist =====

  _activePlaylist() {
    return Array.isArray(this.playbackQueue) ? this.playbackQueue : (this.stationProgram || [])
  }

  _normalizeTrackId(value) {
    const token = String(value || "").trim()
    if (!/^[1-9]\d*$/.test(token)) return null
    const id = Number(token)
    return Number.isSafeInteger(id) && id > 0 ? String(id) : null
  }

  _normalizePlaylistId(value) {
    const id = String(value || "").trim()
    return /^[A-Za-z0-9_-]{1,64}$/.test(id) ? id : null
  }

  _normalizePlaylistName(value) {
    const cleaned = String(value || "")
      .replace(/[\u0000-\u001f\u007f]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
    return Array.from(cleaned).slice(0, JUICEBOX_PLAYLIST_NAME_LIMIT).join("")
  }

  _normalizePlaylistTrackIds(values) {
    if (!Array.isArray(values)) return []
    const seen = new Set()
    return values.reduce((ids, value) => {
      const id = this._normalizeTrackId(value)
      if (!id || seen.has(id) || ids.length >= JUICEBOX_PLAYLIST_LIMIT) return ids
      seen.add(id)
      ids.push(id)
      return ids
    }, [])
  }

  _defaultPlaylistLibrary(ids = []) {
    const now = Date.now()
    return {
      v: JUICEBOX_PLAYLIST_VERSION,
      activeId: JUICEBOX_DEFAULT_PLAYLIST_ID,
      playlists: [{
        id: JUICEBOX_DEFAULT_PLAYLIST_ID,
        name: JUICEBOX_DEFAULT_PLAYLIST_NAME,
        ids: this._normalizePlaylistTrackIds(ids),
        createdAt: now,
        updatedAt: now
      }],
      savedAt: now
    }
  }

  _readLegacySavedPlaylistIds() {
    const raw = localStorage.getItem(JUICEBOX_LEGACY_PLAYLIST_KEY)
    if (raw === null || raw.length > JUICEBOX_LEGACY_PLAYLIST_MAX_BYTES) return []
    try {
      const parsed = JSON.parse(raw)
      if (parsed?.v !== 1) return []
      return this._normalizePlaylistTrackIds(parsed.ids)
    } catch (_) {
      return []
    }
  }

  _readPlaylistLibrary() {
    try {
      const raw = localStorage.getItem(JUICEBOX_PLAYLIST_KEY)
      if (raw !== null) {
        if (raw.length > JUICEBOX_PLAYLIST_MAX_BYTES) {
          this._playlistStorageWarning = "saved playlist data was too large; it was left untouched"
          return this._defaultPlaylistLibrary()
        }
        try {
          const normalized = this._normalizeStoredPlaylistLibrary(JSON.parse(raw))
          if (normalized) return normalized
        } catch (_) {}

        this._playlistStorageWarning = "saved playlist data could not be read; it was left untouched"
        return this._defaultPlaylistLibrary()
      }

      this._playlistLibraryNeedsPersist = true
      return this._defaultPlaylistLibrary(this._readLegacySavedPlaylistIds())
    } catch (_) {
      this._playlistStorageWarning = "this browser could not open saved playlists"
      return this._defaultPlaylistLibrary()
    }
  }

  _normalizeStoredPlaylistLibrary(parsed) {
    if (parsed?.v !== JUICEBOX_PLAYLIST_VERSION || !Array.isArray(parsed.playlists)) return null

    const playlists = []
    const seenIds = new Set()
    parsed.playlists.slice(0, JUICEBOX_PLAYLIST_LIBRARY_LIMIT).forEach((item, index) => {
      const id = this._normalizePlaylistId(item?.id)
      if (!id || seenIds.has(id)) return
      seenIds.add(id)
      const fallbackName = index ? `playlist ${index + 1}` : JUICEBOX_DEFAULT_PLAYLIST_NAME
      const baseName = this._normalizePlaylistName(item?.name) || fallbackName
      const name = this._uniquePlaylistName(baseName, playlists)
      const createdAt = Number.isFinite(Number(item?.createdAt)) ? Number(item.createdAt) : Date.now()
      const updatedAt = Number.isFinite(Number(item?.updatedAt)) ? Number(item.updatedAt) : createdAt
      playlists.push({
        id,
        name,
        ids: this._normalizePlaylistTrackIds(item?.ids),
        createdAt,
        updatedAt
      })
    })

    if (!playlists.length) return null
    const requestedActiveId = this._normalizePlaylistId(parsed.activeId)
    const activeId = playlists.some(playlist => playlist.id === requestedActiveId)
      ? requestedActiveId
      : playlists[0].id
    return {
      v: JUICEBOX_PLAYLIST_VERSION,
      activeId,
      playlists,
      savedAt: Number.isFinite(Number(parsed.savedAt)) ? Number(parsed.savedAt) : Date.now()
    }
  }

  _persistPlaylistLibrary() {
    try {
      const active = this._activePlaylistRecord()
      if (!active) throw new Error("missing active playlist")
      this.playlistLibrary.v = JUICEBOX_PLAYLIST_VERSION
      this.playlistLibrary.activeId = this.activePlaylistId
      this.playlistLibrary.savedAt = Date.now()
      const serialized = JSON.stringify(this.playlistLibrary)
      if (serialized.length > JUICEBOX_PLAYLIST_MAX_BYTES) throw new Error("playlist storage limit")
      localStorage.setItem(JUICEBOX_PLAYLIST_KEY, serialized)
      this._playlistLibraryNeedsPersist = false
      return true
    } catch (_) {
      this._announcePlaylist("this browser could not save the playlists")
      return false
    }
  }

  _persistSavedPlaylistIds() {
    const active = this._activePlaylistRecord()
    if (!active) return false
    const previousIds = [...active.ids]
    const previousUpdatedAt = active.updatedAt
    active.ids = this._normalizePlaylistTrackIds(this.savedPlaylistIds)
    active.updatedAt = Date.now()
    this.savedPlaylistIds = [...active.ids]
    if (this._persistPlaylistLibrary()) return true
    active.ids = previousIds
    active.updatedAt = previousUpdatedAt
    return false
  }

  _playlistRecordById(rawId) {
    const id = this._normalizePlaylistId(rawId)
    return (this.playlistLibrary?.playlists || []).find(playlist => playlist.id === id) || null
  }

  _activePlaylistRecord() {
    return this._playlistRecordById(this.activePlaylistId || this.playlistLibrary?.activeId)
  }

  _playlistNameExists(name, excludingId = null) {
    const normalized = this._normalizePlaylistName(name).toLocaleLowerCase()
    return (this.playlistLibrary?.playlists || []).some(playlist => (
      playlist.id !== excludingId && playlist.name.toLocaleLowerCase() === normalized
    ))
  }

  _uniquePlaylistName(rawName, playlists = this.playlistLibrary?.playlists || []) {
    const base = this._normalizePlaylistName(rawName) || JUICEBOX_DEFAULT_PLAYLIST_NAME
    const names = new Set(playlists.map(playlist => playlist.name.toLocaleLowerCase()))
    if (!names.has(base.toLocaleLowerCase())) return base

    for (let number = 2; number < 1000; number += 1) {
      const suffix = ` (${number})`
      const room = Math.max(1, JUICEBOX_PLAYLIST_NAME_LIMIT - Array.from(suffix).length)
      const candidate = `${Array.from(base).slice(0, room).join("")}${suffix}`
      if (!names.has(candidate.toLocaleLowerCase())) return candidate
    }
    return `playlist ${Date.now().toString(36)}`.slice(0, JUICEBOX_PLAYLIST_NAME_LIMIT)
  }

  _generatePlaylistId() {
    for (let attempt = 0; attempt < 10; attempt += 1) {
      const random = globalThis.crypto?.randomUUID?.().replace(/-/g, "") ||
        Math.random().toString(36).slice(2)
      const id = `p_${Date.now().toString(36)}_${random}`.slice(0, 64)
      if (!this._playlistRecordById(id)) return id
    }
    return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`
  }

  _reloadPlaylistLibraryFromStorage() {
    const previousWarning = this._playlistStorageWarning
    this._playlistStorageWarning = null
    this._playlistLibraryNeedsPersist = false
    const library = this._readPlaylistLibrary()
    if (this._playlistStorageWarning) {
      const message = this._playlistStorageWarning
      this._playlistStorageWarning = previousWarning
      this._announcePlaylist(message)
      return
    }

    this._cancelPlaylistSwipe()
    this._draggedPlaylistId = null
    this._cancelPlaylistHydration()
    this.playlistLibrary = library
    this.activePlaylistId = library.activeId
    this.savedPlaylistIds = [...(this._activePlaylistRecord()?.ids || [])]
    this.savedPlaylist = []
    this._renderSavedPlaylist()
    this._hydrateSavedPlaylist()
  }

  _activatePlaylist(rawId, { announce = false } = {}) {
    const playlist = this._playlistRecordById(rawId)
    if (!playlist || playlist.id === this.activePlaylistId) return false

    this._cancelPlaylistSwipe()
    this._draggedPlaylistId = null
    this._cancelPlaylistHydration()
    if (!this._persistSavedPlaylistIds()) {
      this._renderSavedPlaylist()
      return false
    }
    const previousId = this.activePlaylistId
    const previousIds = [...this.savedPlaylistIds]
    const previousTracks = [...this.savedPlaylist]
    this.activePlaylistId = playlist.id
    this.playlistLibrary.activeId = playlist.id
    this.savedPlaylistIds = [...playlist.ids]
    this.savedPlaylist = []
    if (!this._persistPlaylistLibrary()) {
      this.activePlaylistId = previousId
      this.playlistLibrary.activeId = previousId
      this.savedPlaylistIds = previousIds
      this.savedPlaylist = previousTracks
      this._renderSavedPlaylist()
      if (previousTracks.length !== previousIds.length) this._hydrateSavedPlaylist()
      return false
    }
    this._closePlaylistForm({ restoreFocus: false })
    this._renderSavedPlaylist()
    this._hydrateSavedPlaylist()
    if (announce) this._announcePlaylist(`${playlist.name} selected, ${playlist.ids.length} songs`)
    return true
  }

  _createPlaylist(name, rawIds = [], tracks = []) {
    if ((this.playlistLibrary?.playlists || []).length >= JUICEBOX_PLAYLIST_LIBRARY_LIMIT) {
      this._showPlaylistFormError(`playlist limit is ${JUICEBOX_PLAYLIST_LIBRARY_LIMIT}`)
      return false
    }

    this._cancelPlaylistSwipe()
    this._draggedPlaylistId = null
    this._cancelPlaylistHydration()
    if (!this._persistSavedPlaylistIds()) return false
    const previousId = this.activePlaylistId
    const previousIds = [...this.savedPlaylistIds]
    const previousTracks = [...this.savedPlaylist]
    const ids = this._normalizePlaylistTrackIds(rawIds)
    const now = Date.now()
    const playlist = {
      id: this._generatePlaylistId(),
      name,
      ids,
      createdAt: now,
      updatedAt: now
    }
    this.playlistLibrary.playlists.push(playlist)
    this.playlistLibrary.activeId = playlist.id
    this.activePlaylistId = playlist.id
    this.savedPlaylistIds = [...ids]
    const tracksById = new Map(tracks.map(track => [this._normalizeTrackId(track?.id), track]))
    this.savedPlaylist = ids.map(id => tracksById.get(id)).filter(Boolean)
    if (!this._persistPlaylistLibrary()) {
      this.playlistLibrary.playlists = this.playlistLibrary.playlists.filter(item => item.id !== playlist.id)
      this.playlistLibrary.activeId = previousId
      this.activePlaylistId = previousId
      this.savedPlaylistIds = previousIds
      this.savedPlaylist = previousTracks
      this._renderSavedPlaylist()
      if (previousTracks.length !== previousIds.length) this._hydrateSavedPlaylist()
      return false
    }
    this._renderSavedPlaylist()
    if (this.savedPlaylist.length !== ids.length) this._hydrateSavedPlaylist()
    return true
  }

  _openPlaylistForm(mode, returnFocus) {
    if (!this.hasPlaylistFormTarget || !this.hasPlaylistFormInputTarget) return
    if ((mode === "new" || mode === "copy") &&
        (this.playlistLibrary?.playlists || []).length >= JUICEBOX_PLAYLIST_LIBRARY_LIMIT) {
      this._announcePlaylist(`playlist limit is ${JUICEBOX_PLAYLIST_LIBRARY_LIMIT}`)
      return
    }

    const active = this._activePlaylistRecord()
    this._playlistFormMode = mode
    const manage = returnFocus?.closest?.(".juicebox-playlist-manage")
    this._playlistFormReturnFocus = manage?.querySelector("summary") || returnFocus || null
    if (manage) manage.open = false
    this.playlistFormTarget.hidden = false
    this.playlistFormInputTarget.removeAttribute("aria-invalid")
    if (this.hasPlaylistFormErrorTarget) {
      this.playlistFormErrorTarget.hidden = true
      this.playlistFormErrorTarget.textContent = ""
    }

    const labels = {
      new: ["name your new empty playlist", "create & use"],
      copy: ["save the current songs as", "save a copy"],
      rename: ["rename the active playlist", "rename"]
    }
    const [label, submit] = labels[mode] || labels.new
    if (this.hasPlaylistFormLabelTarget) this.playlistFormLabelTarget.textContent = label
    if (this.hasPlaylistFormSubmitTarget) this.playlistFormSubmitTarget.textContent = submit
    this.playlistFormInputTarget.value = mode === "rename"
      ? (active?.name || "")
      : mode === "copy"
        ? this._uniquePlaylistName(`${active?.name || JUICEBOX_DEFAULT_PLAYLIST_NAME} copy`)
        : ""
    requestAnimationFrame(() => {
      this.playlistFormInputTarget.focus()
      if (mode === "rename") this.playlistFormInputTarget.select()
    })
  }

  _closePlaylistForm({ restoreFocus = false } = {}) {
    if (this.hasPlaylistFormTarget) this.playlistFormTarget.hidden = true
    if (this.hasPlaylistFormInputTarget) {
      this.playlistFormInputTarget.value = ""
      this.playlistFormInputTarget.removeAttribute("aria-invalid")
    }
    if (this.hasPlaylistFormErrorTarget) {
      this.playlistFormErrorTarget.hidden = true
      this.playlistFormErrorTarget.textContent = ""
    }
    const returnFocus = this._playlistFormReturnFocus
    this._playlistFormMode = null
    this._playlistFormReturnFocus = null
    if (restoreFocus) returnFocus?.focus?.()
  }

  _showPlaylistFormError(message) {
    if (!this.hasPlaylistFormInputTarget) return
    this.playlistFormInputTarget.setAttribute("aria-invalid", "true")
    if (this.hasPlaylistFormErrorTarget) {
      this.playlistFormErrorTarget.textContent = message
      this.playlistFormErrorTarget.hidden = false
    }
    this.playlistFormInputTarget.focus()
  }

  _cancelPlaylistHydration() {
    this._playlistHydrationSequence += 1
    this._playlistHydrationAbort?.abort()
    this._playlistHydrationAbort = null
    this._playlistHydrating = false
  }

  async _hydrateSavedPlaylist() {
    const requestedIds = this.savedPlaylistIds.slice(0, JUICEBOX_PLAYLIST_LIMIT)
    const sequence = ++this._playlistHydrationSequence
    this._playlistHydrationAbort?.abort()
    this._playlistHydrationAbort = null

    if (!requestedIds.length) {
      this.savedPlaylist = []
      this._syncSavedPlaybackQueue()
      this._renderSavedPlaylist()
      return
    }

    this._playlistHydrating = true
    this._renderSavedPlaylist()
    const controller = new AbortController()
    this._playlistHydrationAbort = controller

    try {
      const query = encodeURIComponent(requestedIds.join(","))
      const response = await fetch(`/__juicebox/playlist/tracks?ids=${query}`, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin",
        signal: controller.signal
      })
      if (!response.ok) throw new Error(`playlist HTTP ${response.status}`)

      const body = await response.json()
      if (!this._connected || sequence !== this._playlistHydrationSequence) return

      const serverTracks = Array.isArray(body?.tracks) ? body.tracks : []
      const serverById = new Map()
      serverTracks.forEach(track => {
        const id = this._normalizeTrackId(track?.id)
        if (id && requestedIds.includes(id) && track?.audio_url) serverById.set(id, track)
      })

      const existingById = new Map(this.savedPlaylist.map(track => [this._normalizeTrackId(track?.id), track]))
      const requestedSet = new Set(requestedIds)
      const nextIds = this.savedPlaylistIds.filter(id => !requestedSet.has(id) || serverById.has(id))
      this.savedPlaylistIds = nextIds
      this.savedPlaylist = nextIds.map(id => serverById.get(id) || existingById.get(id)).filter(Boolean)
      this._persistSavedPlaylistIds()
      this._syncSavedPlaybackQueue()
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.warn("Juicebox playlist could not be refreshed", error)
        this._announcePlaylist("saved playlist will reload when FM reconnects")
      }
    } finally {
      if (this._connected && sequence === this._playlistHydrationSequence) {
        this._playlistHydrating = false
        this._playlistHydrationAbort = null
        this._renderSavedPlaylist()
      }
    }
  }

  _addTrackToSavedPlaylist(rawId) {
    const id = this._normalizeTrackId(rawId)
    if (!id) return

    if (this.savedPlaylistIds.includes(id)) {
      this._announcePlaylist("that song is already in your playlist")
      return
    }
    if (this.savedPlaylistIds.length >= JUICEBOX_PLAYLIST_LIMIT) {
      this._announcePlaylist(`playlist limit is ${JUICEBOX_PLAYLIST_LIMIT} songs`)
      return
    }

    const track = this.stationPlaylist.find(item => this._normalizeTrackId(item?.id) === id)
    if (!track) return

    this.savedPlaylistIds.push(id)
    this.savedPlaylist.push(track)
    if (!this._persistSavedPlaylistIds()) {
      this.savedPlaylistIds = this.savedPlaylistIds.filter(itemId => itemId !== id)
      this.savedPlaylist = this.savedPlaylist.filter(item => this._normalizeTrackId(item?.id) !== id)
      this._renderSavedPlaylist()
      return
    }
    this._syncSavedPlaybackQueue()
    this._renderSavedPlaylist()
    const playlistName = this._activePlaylistRecord()?.name || JUICEBOX_DEFAULT_PLAYLIST_NAME
    this._announcePlaylist(`${this._normalizeDisplayTitle(track.title) || "song"} added to ${playlistName}`)
  }

  _removeTrackFromSavedPlaylist(rawId) {
    const id = this._normalizeTrackId(rawId)
    const track = this.savedPlaylist.find(item => this._normalizeTrackId(item?.id) === id)
    if (!id || !this.savedPlaylistIds.includes(id)) return

    const previousIds = [...this.savedPlaylistIds]
    const previousTracks = [...this.savedPlaylist]
    this.savedPlaylistIds = this.savedPlaylistIds.filter(itemId => itemId !== id)
    this.savedPlaylist = this.savedPlaylist.filter(item => this._normalizeTrackId(item?.id) !== id)
    if (!this._persistSavedPlaylistIds()) {
      this.savedPlaylistIds = previousIds
      this.savedPlaylist = previousTracks
      this._renderSavedPlaylist()
      return
    }
    this._syncSavedPlaybackQueue()
    this._renderSavedPlaylist()
    const playlistName = this._activePlaylistRecord()?.name || JUICEBOX_DEFAULT_PLAYLIST_NAME
    this._announcePlaylist(`${this._normalizeDisplayTitle(track?.title) || "song"} removed from ${playlistName}`)
  }

  _moveSavedTrack(rawId, delta) {
    const id = this._normalizeTrackId(rawId)
    const from = this.savedPlaylistIds.indexOf(id)
    const to = Math.max(0, Math.min(this.savedPlaylistIds.length - 1, from + delta))
    if (!id || from < 0 || to === from) return

    const ids = [...this.savedPlaylistIds]
    const previousIds = [...this.savedPlaylistIds]
    const previousTracks = [...this.savedPlaylist]
    ids.splice(from, 1)
    ids.splice(to, 0, id)
    this.savedPlaylistIds = ids
    if (!this._applySavedPlaylistOrder(previousIds, previousTracks)) return
    const title = this.savedPlaylist.find(track => this._normalizeTrackId(track?.id) === id)?.title || "song"
    this._announcePlaylist(`${this._normalizeDisplayTitle(title)} moved to ${to + 1}`)
  }

  _dropSavedTrack(sourceRawId, targetRawId, placeAfter) {
    const sourceId = this._normalizeTrackId(sourceRawId)
    const targetId = this._normalizeTrackId(targetRawId)
    if (!sourceId || !targetId || sourceId === targetId) return
    if (!this.savedPlaylistIds.includes(sourceId) || !this.savedPlaylistIds.includes(targetId)) return

    const previousIds = [...this.savedPlaylistIds]
    const previousTracks = [...this.savedPlaylist]
    const ids = this.savedPlaylistIds.filter(id => id !== sourceId)
    let targetIndex = ids.indexOf(targetId)
    if (placeAfter) targetIndex += 1
    ids.splice(targetIndex, 0, sourceId)
    const finalIndex = ids.indexOf(sourceId)
    this.savedPlaylistIds = ids
    if (!this._applySavedPlaylistOrder(previousIds, previousTracks)) return
    const title = this.savedPlaylist.find(track => this._normalizeTrackId(track?.id) === sourceId)?.title || "song"
    this._announcePlaylist(`${this._normalizeDisplayTitle(title)} moved to ${finalIndex + 1}`)
  }

  _applySavedPlaylistOrder(previousIds = null, previousTracks = null) {
    const tracksById = new Map(this.savedPlaylist.map(track => [this._normalizeTrackId(track?.id), track]))
    this.savedPlaylist = this.savedPlaylistIds.map(id => tracksById.get(id)).filter(Boolean)
    if (!this._persistSavedPlaylistIds()) {
      if (previousIds && previousTracks) {
        this.savedPlaylistIds = previousIds
        this.savedPlaylist = previousTracks
      }
      this._renderSavedPlaylist()
      return false
    }
    this._syncSavedPlaybackQueue()
    this._renderSavedPlaylist()
    return true
  }

  _syncSavedPlaybackQueue() {
    if (this.queueSource !== "saved" || this._savedQueuePlaylistId !== this.activePlaylistId) return
    const current = this._currentExtra()
    const currentId = this._normalizeTrackId(current?.id)
    const ordered = [...this.savedPlaylist]
    if (!ordered.length) {
      this._switchToStationQueue(current)
      return
    }
    if (current && !ordered.some(track => this._normalizeTrackId(track?.id) === currentId)) {
      ordered.unshift(current)
      this._transientQueueTrackId = currentId
    } else {
      this._transientQueueTrackId = null
    }
    this.playbackQueue = ordered
    const nextIndex = ordered.findIndex(track => this._normalizeTrackId(track?.id) === currentId)
    this.index = Math.max(0, nextIndex)
  }

  _switchToStationQueue(current) {
    const currentId = this._normalizeTrackId(current?.id)
    const stationIndex = this.stationProgram.findIndex(track => {
      if (current?.is_dj_segment) return track?.dj_segment_id === current?.dj_segment_id
      return !track?.is_dj_segment && this._normalizeTrackId(track?.id) === currentId
    })
    this.queueSource = "station"
    this._savedQueuePlaylistId = null

    if (stationIndex >= 0) {
      this.playbackQueue = this.stationProgram
      this.index = stationIndex
      this._transientQueueTrackId = null
    } else if (current) {
      this.playbackQueue = [current, ...this.stationProgram]
      this.index = 0
      this._transientQueueTrackId = currentId
    } else {
      this.playbackQueue = this.stationProgram
      this.index = 0
      this._transientQueueTrackId = null
    }
  }

  _playSavedTrack(rawId) {
    const id = this._normalizeTrackId(rawId)
    const index = this.savedPlaylist.findIndex(track => this._normalizeTrackId(track?.id) === id)
    if (index < 0 || this._isAdMode) return
    if (this.liveStation) {
      const shareUrl = this._sameOriginUrl(this.savedPlaylist[index]?.share_url)
      if (!shareUrl) {
        this._announcePlaylist("this saved song could not be opened")
        return
      }
      window.location.assign(shareUrl)
      return
    }
    this.playbackQueue = [...this.savedPlaylist]
    this.queueSource = "saved"
    this._savedQueuePlaylistId = this.activePlaylistId
    this._transientQueueTrackId = null
    this.index = index
    this._loadCurrent(true)
    this.closePlaylist()
  }

  _renderPlaylistLibrary() {
    const active = this._activePlaylistRecord()
    if (!active) return
    const playlists = this.playlistLibrary.playlists
    const count = active.ids.length
    const songLabel = count === 1 ? "song" : "songs"

    if (this.hasPlaylistPickerTarget) {
      const picker = this.playlistPickerTarget
      picker.replaceChildren()
      playlists.forEach(playlist => {
        const option = document.createElement("option")
        option.value = playlist.id
        option.textContent = `${playlist.name} — ${playlist.ids.length} ${playlist.ids.length === 1 ? "song" : "songs"}`
        option.selected = playlist.id === active.id
        picker.appendChild(option)
      })
      picker.value = active.id
    }

    if (this.hasPlaylistOpenLabelTarget) {
      this.playlistOpenLabelTarget.textContent = active.name
      const opener = this.playlistOpenLabelTarget.closest(".juicebox-playlist-open")
      opener?.setAttribute("aria-label", `Open ${active.name} playlist, ${count} ${songLabel}`)
    }
    if (this.hasPlaylistNameTarget) {
      this.playlistNameTargets.forEach(target => { target.textContent = active.name })
    }
    if (this.hasPlaylistActiveSummaryTarget) {
      this.playlistActiveSummaryTarget.textContent = `${active.name} • ${count} ${songLabel}`
    }
    if (this.hasPlaylistDeleteTarget) {
      this.playlistDeleteTarget.disabled = playlists.length <= 1
      this.playlistDeleteTarget.title = playlists.length <= 1
        ? "Keep one playlist; clear its songs instead"
        : `Delete ${active.name}`
    }
    if (this.hasPlaylistListTarget) {
      this.playlistListTarget.setAttribute("aria-label", `${active.name} playlist`)
    }
  }

  _renderSavedPlaylist() {
    const count = this.savedPlaylistIds?.length || 0
    this._renderPlaylistLibrary()
    if (this.hasPlaylistCountTarget) {
      this.playlistCountTargets.forEach(target => { target.textContent = String(count) })
    }

    this._refreshStationSavedState()
    if (!this.hasPlaylistListTarget || !this.hasPlaylistEmptyTarget) return

    const list = this.playlistListTarget
    list.replaceChildren()
    const tracksById = new Map(this.savedPlaylist.map(track => [this._normalizeTrackId(track?.id), track]))
    const orderedTracks = this.savedPlaylistIds.map(id => tracksById.get(id)).filter(Boolean)

    if (!orderedTracks.length) {
      this.playlistEmptyTarget.hidden = false
      this.playlistEmptyTarget.textContent = this._playlistHydrating && count
        ? "loading your saved songs…"
        : count
          ? "your songs are saved; reconnect FM to load them"
          : `swipe right to add to ${this._activePlaylistRecord()?.name || JUICEBOX_DEFAULT_PLAYLIST_NAME}, left to remove—or tap + / −`
      return
    }
    this.playlistEmptyTarget.hidden = true

    orderedTracks.forEach((track, index) => {
      const id = this._normalizeTrackId(track.id)
      const row = document.createElement("li")
      row.className = "juicebox-playlist-row"
      row.dataset.extraId = id
      row.draggable = true

      const grip = document.createElement("span")
      grip.className = "juicebox-drag-grip"
      grip.setAttribute("aria-hidden", "true")
      grip.textContent = "⠿"

      const play = document.createElement("button")
      play.type = "button"
      play.className = "juicebox-playlist-track"
      play.textContent = this._normalizeDisplayTitle(track.title) || "untitled track"
      play.setAttribute("aria-label", `Play ${play.textContent}`)
      play.addEventListener("click", () => this._playSavedTrack(id))

      const artist = document.createElement("span")
      artist.className = "juicebox-playlist-artist"
      artist.textContent = this._normalizeDisplayTitle(track.artist) || "unknown artist"
      play.appendChild(artist)

      const actions = document.createElement("div")
      actions.className = "juicebox-playlist-row-actions"
      actions.append(
        this._playlistRowButton("↑", `Move ${play.firstChild.textContent} up`, index === 0, () => this._moveSavedTrack(id, -1)),
        this._playlistRowButton("↓", `Move ${play.firstChild.textContent} down`, index === orderedTracks.length - 1, () => this._moveSavedTrack(id, 1)),
        this._playlistRowButton("×", `Remove ${play.firstChild.textContent}`, false, () => this._removeTrackFromSavedPlaylist(id))
      )

      row.addEventListener("dragstart", event => {
        if (!this.savedPlaylistIds.includes(id)) return event.preventDefault()
        this._draggedPlaylistId = id
        row.classList.add("is-dragging")
        event.dataTransfer.effectAllowed = "move"
        event.dataTransfer.setData("text/plain", id)
      })
      row.addEventListener("dragend", () => {
        this._draggedPlaylistId = null
        row.classList.remove("is-dragging")
        this.playlistListTarget.querySelectorAll(".is-drag-target").forEach(item => item.classList.remove("is-drag-target"))
      })
      row.addEventListener("dragover", event => {
        if (!this._draggedPlaylistId || this._draggedPlaylistId === id) return
        event.preventDefault()
        event.dataTransfer.dropEffect = "move"
        row.classList.add("is-drag-target")
      })
      row.addEventListener("dragleave", () => row.classList.remove("is-drag-target"))
      row.addEventListener("drop", event => {
        event.preventDefault()
        row.classList.remove("is-drag-target")
        const sourceId = this._draggedPlaylistId || event.dataTransfer.getData("text/plain")
        const placeAfter = event.clientY > row.getBoundingClientRect().top + (row.offsetHeight / 2)
        this._dropSavedTrack(sourceId, id, placeAfter)
      })

      row.append(grip, play, actions)
      list.appendChild(row)
    })
  }

  _playlistRowButton(label, ariaLabel, disabled, handler) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "juicebox-playlist-row-button"
    button.textContent = label
    button.setAttribute("aria-label", ariaLabel)
    button.disabled = disabled
    button.addEventListener("click", handler)
    return button
  }

  _refreshStationSavedState() {
    const saved = new Set(this.savedPlaylistIds || [])
    const playlistName = this._activePlaylistRecord()?.name || JUICEBOX_DEFAULT_PLAYLIST_NAME
    this.element.querySelectorAll(".playlist [data-extra-id]").forEach(row => {
      const isSaved = saved.has(this._normalizeTrackId(row.dataset.extraId))
      row.classList.toggle("is-saved", isSaved)
      const button = row.querySelector("[data-playlist-toggle]")
      if (!button) return
      const title = this._normalizeDisplayTitle(row.querySelector(".title")?.textContent) || "song"
      button.disabled = false
      button.setAttribute("aria-pressed", isSaved ? "true" : "false")
      button.setAttribute(
        "aria-label",
        isSaved ? `Remove ${title} from ${playlistName}` : `Add ${title} to ${playlistName}`
      )
      button.textContent = isSaved ? "−" : "+"
      button.title = isSaved ? `Remove from ${playlistName}` : `Add to ${playlistName}`
    })
  }

  _announcePlaylist(message) {
    const dialogOpen = this.hasPlaylistOverlayTarget && this.playlistOverlayTarget.open
    if (this.hasPlaylistStatusTarget) {
      this.playlistStatusTarget.textContent = dialogOpen ? message : ""
    }
    if (!this.hasPlaylistNoticeTarget) return
    if (dialogOpen) {
      this.playlistNoticeTarget.hidden = true
      this.playlistNoticeTarget.textContent = ""
      return
    }
    this.playlistNoticeTarget.textContent = message
    this.playlistNoticeTarget.hidden = false
    if (this._playlistNoticeTimer) window.clearTimeout(this._playlistNoticeTimer)
    this._playlistNoticeTimer = window.SiteNotices.show(this.playlistNoticeTarget)
  }

  _playlistSwipeThreshold(surface) {
    const width = Number(surface?.getBoundingClientRect?.().width || 0)
    return Math.round(Math.max(44, Math.min(64, width * 0.15)))
  }

  _playlistSwipeCanCommit(id, action) {
    const isSaved = this.savedPlaylistIds.includes(id)
    return action === "remove"
      ? isSaved
      : !isSaved && this.savedPlaylistIds.length < JUICEBOX_PLAYLIST_LIMIT
  }

  _suppressPlaylistTrackClick(id) {
    this._suppressTrackClickId = id
    window.setTimeout(() => {
      if (this._suppressTrackClickId === id) this._suppressTrackClickId = null
    }, 700)
  }

  _finishPlaylistSwipe(swipe, { action = null } = {}) {
    if (this._playlistSwipe === swipe) this._playlistSwipe = null
    try { swipe.surface?.releasePointerCapture?.(swipe.pointerId) } catch (_) {}
    swipe.row?.classList.remove("is-swiping", "is-swipe-ready", "is-swipe-right", "is-swipe-left")
    swipe.link?.style.removeProperty("--juicebox-swipe-x")
    if (!action) return

    const className = action === "remove" ? "is-swipe-removed" : "is-swipe-added"
    swipe.row?.classList.add(className)
    window.setTimeout(() => swipe.row?.classList.remove(className), 520)
  }

  _cancelPlaylistSwipe() {
    const swipe = this._playlistSwipe
    if (!swipe) return
    this._finishPlaylistSwipe(swipe)
  }

  // ===== Shared Longplay station clock =====

  changeLanguage(event) {
    if (!this.liveStation || !this.longplayLanguage.choose(event.target.value)) return
    this._clearLivePrefetch()
    this._primeNextLiveAudio()
    if (this.hasLanguageStatusTarget) {
      this.languageStatusTarget.textContent = this.longplayLanguage.preferred === "es"
        ? "Español a partir de la próxima pausa disponible."
        : "English from the next available break."
    }
  }

  _startLanguagePolling() {
    const url = this._sameOriginUrl(this.languagesUrlValue)
    if (!url) return
    const poll = async () => {
      if (!this._connected || document.hidden || this._languageAbort) return
      const abort = new AbortController()
      this._languageAbort = abort
      try {
        const response = await fetch(url, {credentials: "same-origin", cache: "no-store", signal: abort.signal})
        if (response.ok && this._connected && this.longplayLanguage.update(await response.json(), this.stationProgramVersionValue)) {
          this._refreshLanguageStatus()
          this._primeNextLiveAudio()
        }
      } catch (_) {
        // Keep the last verified pair map and the original stream available.
      } finally {
        if (this._languageAbort === abort) this._languageAbort = null
      }
    }
    poll()
    this._languageTimer = window.setInterval(poll, 30000)
  }

  _refreshLanguageStatus(item = this._activePlaylist()[this.index]) {
    if (this.liveStation && this.hasLanguageStatusTarget) {
      this.languageStatusTarget.textContent = this.longplayLanguage.statusMessage(item)
    }
  }

  _startLiveStation() {
    const position = this._liveProgramPosition()
    if (!position) return

    this._configureLiveMediaSession()
    this._startLiveProgramPolling()
    this.index = position.index
    this.playbackQueue = this.stationProgram
    this.queueSource = "station"
    this._syncLiveStation({ autoplay: !this._liveUserPaused, hard: true })
    this._liveSyncTimer = window.setInterval(() => {
      this._syncLiveStation({ autoplay: !this._liveUserPaused, hard: false })
    }, LONGPLAY_SYNC_INTERVAL_MS)
  }

  _startLiveProgramPolling() {
    const version = String(this.stationProgramVersionValue || "")
    const url = this._sameOriginUrl(this.stationVersionUrlValue)
    if (!/^[a-f0-9]{64}$/.test(version) || !url) return

    this._liveVersionUrl = url
    this._liveLoadedVersion = version
    this._pollLiveProgramVersion(url, version)
    this._liveVersionTimer = window.setInterval(() => {
      this._pollLiveProgramVersion(url, version)
    }, LONGPLAY_PROGRAM_POLL_MS)
  }

  _pollLiveProgramVersion(url, loadedVersion) {
    if (!url || !/^[a-f0-9]{64}$/.test(String(loadedVersion || ""))) return
    if (!this._connected || document.hidden || this._liveVersionFetchActive || this._liveProgramRefreshStarted) return
    this._liveVersionFetchActive = true
    this._liveVersionAbort = new AbortController()
    let responseClockSampled = false

    fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-cache",
      signal: this._liveVersionAbort.signal
    })
      .then(response => {
        responseClockSampled = this._sampleLiveServerClock(
          response.headers.get("X-Longplay-Server-Time-Ms")
        )
        if (response.status === 304) return null
        return response.ok ? response.json() : null
      })
      .then(payload => {
        if (!payload) return

        const serverNowMs = Number(payload.server_now_ms)
        if (!responseClockSampled) this._sampleLiveServerClock(serverNowMs)
        const currentVersion = String(payload?.program_version || "")
        if (!/^[a-f0-9]{64}$/.test(currentVersion)) return
        const activeEpochMs = Number(payload?.program_epoch_ms)
        const loadedEpochMs = Number(this.stationEpochValue || 0)
        const epochChanged = Number.isFinite(activeEpochMs) && activeEpochMs > 0 &&
          Number.isFinite(loadedEpochMs) && Math.abs(activeEpochMs - loadedEpochMs) >= 1
        if (currentVersion === loadedVersion && !epochChanged) {
          this._armLiveProgramBoundaryPoll(url, loadedVersion, payload)
          return
        }

        this._liveProgramRefreshStarted = true
        this._storeLiveReloadPauseState()
        if (this._liveVersionTimer) window.clearInterval(this._liveVersionTimer)
        this._liveVersionTimer = null
        if (this._liveBoundaryTimer) window.clearTimeout(this._liveBoundaryTimer)
        this._liveBoundaryTimer = null
        this._liveBoundarySignature = null
        window.location.reload()
      })
      .catch(error => {
        if (error?.name !== "AbortError") console.debug("Longplay program-version poll failed", error)
      })
      .finally(() => {
        this._liveVersionFetchActive = false
        this._liveVersionAbort = null
      })
  }

  _armLiveProgramBoundaryPoll(url, loadedVersion, payload) {
    const pendingRevision = String(payload?.pending_revision || "")
    const pendingVersion = String(payload?.pending_schedule_version || "")
    const activatesAtMs = Number(payload?.pending_activates_at_ms)
    const serverNowMs = Number(payload?.server_now_ms)
    const valid = /^lpp_[a-f0-9]{24}$/.test(pendingRevision) &&
      /^[a-f0-9]{64}$/.test(pendingVersion) &&
      Number.isFinite(activatesAtMs) && Number.isFinite(serverNowMs)

    if (!valid) {
      if (this._liveBoundaryTimer) window.clearTimeout(this._liveBoundaryTimer)
      this._liveBoundaryTimer = null
      this._liveBoundarySignature = null
      return
    }

    const signature = `${pendingRevision}:${pendingVersion}:${activatesAtMs}`
    if (signature === this._liveBoundarySignature && this._liveBoundaryTimer) return

    if (this._liveBoundaryTimer) window.clearTimeout(this._liveBoundaryTimer)
    this._liveBoundarySignature = signature
    // The activation worker owns the authoritative switch. This timer merely
    // asks for status just after the planned audio boundary; it never swaps a
    // playlist or marks a listener request fulfilled from browser state.
    const untilBoundaryPoll = activatesAtMs - serverNowMs + 750
    // Once the boundary is in the past, the durable activation clock may be
    // rescheduling a missed cut. Do not let every listener turn an unchanged
    // pending ETag into a four-requests-per-second retry loop.
    const delay = untilBoundaryPoll > 0
      ? Math.max(250, Math.min(2147483000, untilBoundaryPoll))
      : LONGPLAY_PROGRAM_POLL_MS
    this._liveBoundaryTimer = window.setTimeout(() => {
      this._liveBoundaryTimer = null
      this._liveBoundarySignature = null
      this._pollLiveProgramVersion(url, loadedVersion)
    }, delay)
  }

  _sameOriginUrl(rawUrl) {
    try {
      const url = new URL(String(rawUrl || ""), window.location.origin)
      return url.origin === window.location.origin ? url.toString() : null
    } catch (_) {
      return null
    }
  }

  _sampleLiveServerClock(rawServerNowMs) {
    const serverNowMs = Number(rawServerNowMs)
    if (!Number.isFinite(serverNowMs) || serverNowMs <= 0) return false

    this._liveServerOffsetMs = serverNowMs - Date.now()
    return true
  }

  _storeLiveReloadPauseState() {
    try {
      sessionStorage.setItem(LONGPLAY_RELOAD_PAUSE_KEY, this._liveUserPaused ? "1" : "0")
    } catch (_) {}
  }

  _consumeLiveReloadPauseState() {
    try {
      const paused = sessionStorage.getItem(LONGPLAY_RELOAD_PAUSE_KEY) === "1"
      sessionStorage.removeItem(LONGPLAY_RELOAD_PAUSE_KEY)
      return paused
    } catch (_) {
      return false
    }
  }

  _liveProgramPosition(atMs = Date.now() + this._liveServerOffsetMs) {
    if (!this.liveStation || !this.stationProgram.length) return null
    const epochMs = Number(this.stationEpochValue || 0)
    if (!Number.isFinite(epochMs) || epochMs <= 0) return null

    const durations = this.stationProgram.map(track => Number(track?.duration_seconds || 0))
    if (durations.some(duration => !Number.isFinite(duration) || duration <= 0)) return null
    const total = durations.reduce((sum, duration) => sum + duration, 0)
    if (!(total > 0)) return null

    const elapsedSeconds = (atMs - epochMs) / 1000
    const cycleOffset = ((elapsedSeconds % total) + total) % total
    let cursor = 0
    for (let index = 0; index < durations.length; index += 1) {
      const boundary = cursor + durations[index]
      if (cycleOffset < boundary) {
        return { index, offset: cycleOffset - cursor, duration: durations[index], total }
      }
      cursor = boundary
    }
    return { index: 0, offset: 0, duration: durations[0], total }
  }

  _syncLiveStation({ autoplay = false, hard = false } = {}) {
    if (!this.liveStation) return
    const expected = this._liveProgramPosition()
    if (!expected) return

    // A short loading/clock discrepancy must not replace the last spoken
    // word with the next song. Finish a small, actively playing tail, then
    // let the normal whole-song catch-up recover the shared clock.
    const playingMedia = this._activeMedia()
    if (this._shouldFinishLiveSpeech(expected, playingMedia)) {
      playingMedia.playbackRate = 1
      return
    }

    // A media element can emit `ended` a few milliseconds before the shared
    // station clock crosses the same item boundary. Re-seeking that finished
    // file makes its tail chatter in a tight loop (most visible on short DJ
    // recaps). Hold silence until the authoritative clock advances instead.
    if (this._liveEndedIndex === expected.index) {
      this.index = expected.index
      this._syncLiveDjBed(expected, { hard: false })
      this._syncLiveCatalog(this._currentExtra())
      return
    }
    if (this._liveEndedIndex !== null) this._clearLiveEndedBoundary()

    if (this.index !== expected.index || !this._activeMedia()) {
      const nextTrack = this.stationProgram[expected.index]
      const startWholeTrack = this._canStartWholeLiveTrack(nextTrack, expected)
      this.index = expected.index
      this.playbackQueue = this.stationProgram
      this.queueSource = "station"
      this._liveWholeTrackIndex = startWholeTrack ? expected.index : null
      this._liveWholeTrackStartApplied = false
      this._livePendingHardSync = true
      this._loadCurrent(autoplay && !this._liveUserPaused)
      return
    }

    const media = this._activeMedia()
    if (!media) return
    if (hard || this._livePendingHardSync) {
      this._livePendingHardSync = false
      this._applyLiveOffset(media, expected)
      this._syncLiveDjBed(expected, { hard: true })
    } else {
      this._applyLiveDrift(media, expected)
      this._syncLiveDjBed(expected, { hard: false })
    }
    this._syncLiveCatalog(this._currentExtra())

    if (autoplay && !this._liveUserPaused && media.paused) {
      this._safePlay(media, "longplay-live")
      this._resumeLiveDjBed()
    }
  }

  _handleLiveMediaEnded() {
    if (!this.liveStation) return
    const expected = this._liveProgramPosition()
    if (!expected) return

    if (expected.index !== this.index) {
      this._clearLiveEndedBoundary()
      this._syncLiveStation({ autoplay: !this._liveUserPaused, hard: true })
      return
    }

    this._liveEndedIndex = expected.index
    if (this._liveEndedTimer) window.clearTimeout(this._liveEndedTimer)
    const remainingMs = Math.max(
      LONGPLAY_BOUNDARY_GRACE_MS,
      Math.ceil((expected.duration - expected.offset) * 1000) + LONGPLAY_BOUNDARY_GRACE_MS
    )
    this._liveEndedTimer = window.setTimeout(() => {
      this._liveEndedTimer = null
      const current = this._liveProgramPosition()
      if (current?.index === this._liveEndedIndex) {
        this._handleLiveMediaEnded()
        return
      }
      this._clearLiveEndedBoundary()
      this._syncLiveStation({ autoplay: !this._liveUserPaused, hard: true })
    }, Math.min(remainingMs, 2147483000))
  }

  _clearLiveEndedBoundary() {
    if (this._liveEndedTimer) window.clearTimeout(this._liveEndedTimer)
    this._liveEndedTimer = null
    this._liveEndedIndex = null
  }

  _applyLiveOffset(media, expected) {
    if (!media || media.readyState < 1) {
      this._livePendingHardSync = true
      return
    }
    if (this._preservingWholeLiveTrack(expected)) {
      if (!this._liveWholeTrackStartApplied) {
        try { media.currentTime = 0 } catch (_) {
          this._livePendingHardSync = true
          return
        }
        this._liveWholeTrackStartApplied = true
      }
      this._applyWholeTrackCatchup(media, expected)
      this._updateProgressUI(media)
      return
    }
    const target = Math.max(0, Math.min(expected.offset, Math.max(0, expected.duration - 0.05)))
    try { media.currentTime = target } catch (_) {
      this._livePendingHardSync = true
      return
    }
    media.playbackRate = 1
    this._updateProgressUI(media)
  }

  _applyLiveDrift(media, expected) {
    if (!media || media.readyState < 1 || media.paused) return
    if (this._preservingWholeLiveTrack(expected)) {
      this._applyWholeTrackCatchup(media, expected)
      return
    }
    const drift = Number(media.currentTime || 0) - expected.offset
    const absoluteDrift = Math.abs(drift)
    if (absoluteDrift >= LONGPLAY_HARD_DRIFT_SECONDS) {
      this._applyLiveOffset(media, expected)
    } else if (absoluteDrift >= LONGPLAY_SOFT_DRIFT_SECONDS) {
      media.playbackRate = drift < 0 ? LONGPLAY_FAST_RATE : LONGPLAY_SLOW_RATE
    } else {
      media.playbackRate = 1
    }
  }

  _preservingWholeLiveTrack(expected) {
    return this._liveWholeTrackIndex === expected?.index &&
      this.index === expected?.index
  }

  _canStartWholeLiveTrack(track, expected) {
    const offset = Number(expected?.offset)
    const duration = Number(expected?.duration)
    if (!Number.isFinite(offset) || !Number.isFinite(duration)) return false
    if (offset < 0 || duration <= 0 || offset >= duration) return false
    if (offset > LONGPLAY_WHOLE_TRACK_START_GRACE_SECONDS) return false
    if (track?.is_dj_segment && offset > LONGPLAY_SPEECH_START_GRACE_SECONDS) return false

    // Restarting at zero is only honest "play the whole song" behavior if the
    // capped catch-up rate can still reach the shared station boundary. A
    // fixed grace alone is unsafe for short tracks and can sacrifice the tail.
    const remainingClock = duration - offset
    const requiredRate = duration / remainingClock
    return requiredRate <= LONGPLAY_WHOLE_TRACK_MAX_CATCHUP_RATE
  }

  _shouldFinishLiveSpeech(expected, media) {
    if (!this.stationProgram[this.index]?.is_dj_segment || !media || media.paused || media.ended || media.readyState < 2) return false
    if (expected.index !== (this.index + 1) % this.stationProgram.length || expected.index === this.index) return false
    const remaining = Number(media.duration) - Number(media.currentTime)
    return Number.isFinite(remaining) && remaining > 0 && remaining <= LONGPLAY_SPEECH_FINISH_GRACE_SECONDS &&
      Number.isFinite(expected.offset) && expected.offset >= 0 && expected.offset <= LONGPLAY_SPEECH_FINISH_GRACE_SECONDS
  }

  _applyWholeTrackCatchup(media, expected) {
    const current = Number(media.currentTime || 0)
    const behind = expected.offset - current
    if (behind <= LONGPLAY_WHOLE_TRACK_SYNCED_DRIFT_SECONDS) {
      this._liveWholeTrackIndex = null
      this._liveWholeTrackStartApplied = false
      media.playbackRate = 1
      return
    }

    const measuredDuration = Number(media.duration || 0)
    const mediaDuration = Number.isFinite(measuredDuration) && measuredDuration > 0
      ? measuredDuration
      : expected.duration
    const remainingMedia = Math.max(0.05, mediaDuration - current)
    const remainingClock = Math.max(0.05, expected.duration - expected.offset)
    const requestedRate = remainingMedia / remainingClock

    // Loading or buffering can consume the margin that made a whole-track
    // restart safe. Rejoin the authoritative clock instead of preserving the
    // beginning only to cut off the end at the next program boundary.
    if (!Number.isFinite(requestedRate) || requestedRate > LONGPLAY_WHOLE_TRACK_MAX_CATCHUP_RATE) {
      this._liveWholeTrackIndex = null
      this._liveWholeTrackStartApplied = false
      media.playbackRate = 1
      this._applyLiveOffset(media, expected)
      return
    }

    media.playbackRate = Math.max(
      1,
      requestedRate
    )
  }

  _primeNextLiveAudio() {
    if (!this.liveStation || !this.stationProgram.length) return
    if (!this._livePlaybackIntent) {
      this._clearLivePrefetch()
      return
    }
    if (this._liveDjBedVoiceIndex === this.index && this._liveDjBedUrl) return
    const nextIndex = (Number(this.index) + 1) % this.stationProgram.length
    const nextTrack = this.stationProgram[nextIndex]
    const coverKind = String(nextTrack?.cover_art_kind || nextTrack?.cover_kind || "").toLowerCase()
    const url = coverKind === "video" ? null : this._safeHttpUrl(this.longplayLanguage.source(nextTrack).url)
    if (!url) {
      this._clearLivePrefetch()
      return
    }
    if (this._livePrefetchAudio && this._livePrefetchUrl === url) return

    this._clearLivePrefetch()
    const prefetch = document.createElement("audio")
    prefetch.preload = "auto"
    prefetch.crossOrigin = "anonymous"
    prefetch.src = url
    this._livePrefetchAudio = prefetch
    this._livePrefetchUrl = url
    try { prefetch.load() } catch (_) {}
  }

  _clearLivePrefetch() {
    const prefetch = this._livePrefetchAudio
    this._livePrefetchAudio = null
    this._livePrefetchUrl = null
    if (!prefetch) return
    try {
      prefetch.pause()
      prefetch.removeAttribute("src")
      prefetch.load()
    } catch (_) {}
  }

  _liveDjMixFor(track) {
    if (!track?.is_dj_segment || track.dj_mix_mode !== LONGPLAY_DJ_MIX_MODE) return null
    if (track.dj_restart_next_track !== true) return null

    const voiceVolume = Number(track.dj_voice_volume)
    const bedVolume = Number(track.dj_bed_volume)
    if (!Number.isFinite(voiceVolume) || voiceVolume < 0 || voiceVolume > 1) return null
    if (!Number.isFinite(bedVolume) || bedVolume < 0 || bedVolume > 1) return null

    return { voiceVolume, bedVolume }
  }

  _nextLiveMusicTrack(afterIndex = this.index) {
    if (!this.liveStation || !this.stationProgram.length) return null
    const origin = Number(afterIndex)
    if (!Number.isInteger(origin)) return null

    for (let distance = 1; distance <= this.stationProgram.length; distance += 1) {
      const index = (origin + distance) % this.stationProgram.length
      const track = this.stationProgram[index]
      if (track && !track.is_dj_segment) return { index, track }
    }
    return null
  }

  _liveDjBedSource(track) {
    const coverKind = String(track?.cover_art_kind || track?.cover_kind || "").toLowerCase()
    const rawUrl = coverKind === "video"
      ? (track?.cover_art_video_url || track?.cover_art_url)
      : track?.audio_url
    return this._safeHttpUrl(rawUrl)
  }

  _prepareLiveDjBed(djTrack, { autoplay = false } = {}) {
    if (!this.liveStation || !this.hasBedAudioTarget) return
    if (!this._livePlaybackIntent) {
      this._stopLiveDjBed()
      return
    }
    const mix = this._liveDjMixFor(djTrack)
    const next = this._nextLiveMusicTrack(this.index)
    const url = next && this._liveDjBedSource(next.track)
    if (!mix || !next || !url) {
      this._stopLiveDjBed()
      return
    }

    const bed = this.bedAudioTarget
    this._liveDjBedVoiceIndex = this.index
    this._liveDjBedTrackIndex = next.index
    this._liveDjBedUrl = url
    bed.volume = mix.bedVolume
    bed.muted = this._readMutePreference()
    bed.loop = false
    bed.playbackRate = 1
    if (bed.src !== url) {
      bed.src = url
      try { bed.load() } catch (_) {}
    }
    if (this.hasAudioTarget) this.audioTarget.volume = mix.voiceVolume

    const expected = this._liveProgramPosition()
    if (expected) this._syncLiveDjBed(expected, { hard: true })
    if (autoplay && !this._liveUserPaused) this._safePlay(bed, "longplay-dj-bed")
  }

  _syncLiveDjBed(expected, { hard = false } = {}) {
    if (!this.liveStation || !this.hasBedAudioTarget) return
    if (this._liveDjBedVoiceIndex !== this.index || expected?.index !== this.index) return
    const current = this.stationProgram[this.index]
    if (!this._liveDjMixFor(current)) return

    const bed = this.bedAudioTarget
    if (!bed.src || bed.readyState < 1) return
    const measuredDuration = Number(bed.duration || 0)
    const upper = Number.isFinite(measuredDuration) && measuredDuration > 0
      ? Math.max(0, measuredDuration - 0.05)
      : Number.POSITIVE_INFINITY
    const target = Math.max(0, Math.min(Number(expected.offset || 0), upper))
    const drift = Number(bed.currentTime || 0) - target
    if (hard || Math.abs(drift) >= LONGPLAY_DJ_BED_HARD_DRIFT_SECONDS) {
      try { bed.currentTime = target } catch (_) {}
    }
    bed.playbackRate = 1
  }

  _resumeLiveDjBed() {
    if (!this.liveStation || this._liveUserPaused || !this.hasBedAudioTarget) return
    const current = this.stationProgram[this.index]
    if (!this._liveDjMixFor(current)) return
    if (this._liveDjBedVoiceIndex !== this.index || !this._liveDjBedUrl) {
      this._prepareLiveDjBed(current, { autoplay: true })
      return
    }
    if (this.bedAudioTarget.paused) this._safePlay(this.bedAudioTarget, "longplay-dj-bed-resume")
  }

  _pauseLiveDjBed() {
    if (!this.hasBedAudioTarget) return
    try { this.bedAudioTarget.pause() } catch (_) {}
  }

  _stopLiveDjBed() {
    this._liveDjBedVoiceIndex = null
    this._liveDjBedTrackIndex = null
    this._liveDjBedUrl = null
    if (!this.hasBedAudioTarget) return

    const bed = this.bedAudioTarget
    try {
      bed.pause()
      bed.removeAttribute("src")
      bed.load()
    } catch (_) {}
  }

  _correctLiveDrift(media) {
    const now = performance.now()
    if (this._lastLiveDriftCheckAt && now - this._lastLiveDriftCheckAt < 1500) return
    this._lastLiveDriftCheckAt = now
    const expected = this._liveProgramPosition()
    if (!expected) return
    if (expected.index !== this.index) {
      this._syncLiveStation({ autoplay: !this._liveUserPaused, hard: true })
      return
    }
    this._applyLiveDrift(media, expected)
    this._syncLiveDjBed(expected, { hard: false })
  }

  _syncLiveCatalog(currentTrack) {
    if (!this.liveStation || !currentTrack) return
    const list = this.element.querySelector("ol.playlist")
    if (!list) return
    const rows = Array.from(list.querySelectorAll(":scope > li[data-longplay-program-index]"))
    const currentProgramIndex = Number(this.index)
    if (!Number.isInteger(currentProgramIndex) || currentProgramIndex < 0) return

    const currentRowIndex = rows.findIndex(
      row => Number(row.dataset.longplayProgramIndex) === currentProgramIndex
    )
    if (currentRowIndex < 0) return

    const ordered = rows.slice(currentRowIndex).concat(rows.slice(0, currentRowIndex))
    let foundUpcomingMusic = false

    ordered.forEach((row, index) => {
      list.appendChild(row)
      const isCurrent = index === 0
      row.classList.toggle("active", isCurrent)
      if (isCurrent) row.setAttribute("aria-current", "true")
      else row.removeAttribute("aria-current")

      if (row.classList.contains("longplay-break-row")) return

      this._syncLiveAlbumLink(row, { isCurrent })
      const status = row.querySelector(".longplay-row-status")
      if (!status) return
      if (isCurrent) {
        status.textContent = "ON AIR"
      } else if (!foundUpcomingMusic) {
        status.textContent = "UP NEXT"
        foundUpcomingMusic = true
      } else {
        status.textContent = "LATER"
      }
    })
  }

  _syncLiveAlbumLink(row, { isCurrent }) {
    const albumHref = this._safeHttpUrl(row?.dataset?.albumHref)
    const shouldLink = !isCurrent && Boolean(albumHref)
    const title = row?.querySelector?.(".longplay-live-row > .title")
    if (!title) return

    const isLink = title.tagName === "A" && title.dataset.longplayAlbumLink === "true"
    if (isLink === shouldLink) {
      if (shouldLink) title.setAttribute("href", albumHref)
      return
    }

    const replacement = document.createElement(shouldLink ? "a" : "span")
    replacement.className = title.className
    replacement.classList.toggle("longplay-album-link", shouldLink)
    while (title.firstChild) replacement.appendChild(title.firstChild)
    if (shouldLink) {
      replacement.setAttribute("href", albumHref)
      replacement.dataset.longplayAlbumLink = "true"
    }
    title.replaceWith(replacement)
  }

  _configureLiveMediaSession() {
    if (!("mediaSession" in navigator)) return
    const stayLive = () => this._syncLiveStation({ autoplay: !this._liveUserPaused, hard: true })
    ;["nexttrack", "previoustrack", "seekbackward", "seekforward", "seekto"].forEach(action => {
      try { navigator.mediaSession.setActionHandler(action, stayLive) } catch (_) {}
    })
    try {
      navigator.mediaSession.setActionHandler("play", () => {
        this._liveUserPaused = false
        this._livePlaybackIntent = true
        this._syncLiveStation({ autoplay: true, hard: true })
      })
      navigator.mediaSession.setActionHandler("pause", () => {
        this._liveUserPaused = true
        this._activeMedia()?.pause?.()
      })
    } catch (_) {}
  }

  _clearLiveMediaSession() {
    if (!("mediaSession" in navigator)) return
    ;["nexttrack", "previoustrack", "seekbackward", "seekforward", "seekto", "play", "pause"].forEach(action => {
      try { navigator.mediaSession.setActionHandler(action, null) } catch (_) {}
    })
  }

  // ===== Core music flow =====

  _advance(delta) {
    if (this.liveStation) {
      this._syncLiveStation({ autoplay: !this._liveUserPaused, hard: true })
      return
    }
    let list = this._activePlaylist()
    if (!list.length) return
    const currentId = this._normalizeTrackId(list[this.index]?.id)
    if (currentId && currentId === this._transientQueueTrackId) {
      list = list.filter(track => this._normalizeTrackId(track?.id) !== currentId)
      this.playbackQueue = list
      this._transientQueueTrackId = null
      if (!list.length) return
      this.index = delta < 0 ? list.length - 1 : 0
      this._loadCurrent(true)
      return
    }
    this.index = (this.index + delta + list.length) % list.length
    this._loadCurrent(true)
  }

  _loadCurrent(autoplay = false) {
    if (this._isAdMode) return // ads own the UI while active
    const list = this._activePlaylist()
    const t = list[this.index]
    if (!t) return
    const languageSource = this.liveStation ? this.longplayLanguage.source(t, {commit: true}) : {url: t.audio_url}
    this._refreshLanguageStatus(t)

    if (!this.liveStation || !t.is_dj_segment) this._stopLiveDjBed()

    this._removeAdPlayOverlay()

    this._reportedPlayForCurrent = false
    this._reportedDjOnAirForCurrent = false
    this._playThreshold = 10
    this._refreshTrackToggleUI()

    // ticker
    this._setTicker(t.title || "", t.artist || "", t.album || "")

    // album / artist / lyrics
    if (this.hasAlbumTarget) {
      this.albumTarget.textContent = t.album || ""
      const albumHref = this._safeHttpUrl(t.album_href)
      if (albumHref) this.albumTarget.href = albumHref
      else this.albumTarget.removeAttribute("href")
    }
    if (this.hasArtistTarget) {
      this.artistTarget.textContent = t.artist || ""
      const artistHref = this._safeHttpUrl(t.artist_href)
      if (artistHref) this.artistTarget.href = artistHref
      else this.artistTarget.removeAttribute("href")
    }
    if (this.hasLyricsTarget) this.lyricsTarget.textContent = t.lyrics || ""

    // artwork & visuals
    if (this.hasAlbumCoverTarget) {
      const absCover = this._safeHttpUrl(t.album_cover_url)
      if (absCover && this.albumCoverTarget.src !== absCover) this.albumCoverTarget.src = absCover
      if (!absCover) this.albumCoverTarget.removeAttribute("src")
    }
    if (this.hasAlbumLinkTarget) {
      const albumLinkHref = this._safeHttpUrl(t.album_href)
      if (albumLinkHref) this.albumLinkTarget.href = albumLinkHref
      else this.albumLinkTarget.removeAttribute("href")
    }
    this._updateVisualForTrack(t)

    // --- PRIMARY VIDEO CASE: if extra.cover_art is a video, use that video as the primary (with audio)
    // A DJ item inherits its source track artwork metadata. It must still use
    // the governed speech attachment as the primary audio, never a video
    // cover's soundtrack.
    const coverKind = t.is_dj_segment
      ? ""
      : (t.cover_art_kind || t.cover_kind || "").toLowerCase()
    const coverVideoUrl = this._safeHttpUrl(t.cover_art_video_url || t.cover_art_url)
    let audioUrl = null
    if (coverKind === "video" && coverVideoUrl) {
      // Stop audio element so we don't double play
      if (this.hasAudioTarget) {
        this.audioTarget.pause()
        this.audioTarget.removeAttribute("src")
      }
      this._usePrimaryVideo(coverVideoUrl, autoplay)
    } else {
      // AUDIO primary (mp3, etc.)
      // A previous video track may have been removed from the DOM by the
      // visual refresh, but it must also stop being the active media source.
      this._teardownPrimaryVideo()
      audioUrl = this._safeHttpUrl(languageSource.url)
      if (this.hasAudioTarget && audioUrl) {
        if (!this.audioTarget.crossOrigin) {
          this.audioTarget.crossOrigin = "anonymous"
        }
        if (this.audioTarget.src !== audioUrl) {
          this.audioTarget.src = audioUrl
        }
        this.audioTarget.volume = t.is_dj_segment
          ? (this._liveDjMixFor(t)?.voiceVolume ?? 1)
          : 1
        this.audioTarget.loop = this.liveStation ? false : this.repeatOne
        // respect stored mute
        const muted = this._readMutePreference()
        this.audioTarget.muted = muted

        // ensure we DO NOT count until the browser actually starts playback
        this._pauseListenTimer()

        if (autoplay) {
          this._safePlay(this.audioTarget, "music")
        }
        this._updateProgressUI(this.audioTarget)
      }
    }
    if (this.liveStation && t.is_dj_segment) {
      this._prepareLiveDjBed(t, { autoplay })
    }

    // Update track-specific controls even when this song came from another station.
    const id = this._normalizeTrackId(t.id)
    const share = this._safeHttpUrl(t.share_url)
    if (this.hasOriginalLinkTarget && id) {
      this.originalLinkTarget.href = `https://313.cash/extras/${encodeURIComponent(id)}?track_ownership=1`
    }
    if (this.hasShareBtnTarget && share) this.shareBtnTarget.dataset.shareUrl = share

    // pushState
    if (share && !this.liveStation) {
      if (window.location.toString() !== share) {
        window.history.pushState({}, "", share)
      }
    }

    // Prefer the exact Longplay program row because a speech break can share
    // its source Extra ID with a different music row. Ordinary FM keeps its
    // established ID-based active-row behavior.
    this.element.querySelectorAll("ol.playlist > li").forEach(el => {
      el.classList.remove("active")
      el.removeAttribute("aria-current")
      el.querySelector(".playlist-btn")?.removeAttribute("aria-current")
    })
    const programIndex = Number(this.index)
    const programActive = Number.isInteger(programIndex) && programIndex >= 0
      ? this.element.querySelector(`ol.playlist > li[data-longplay-program-index="${CSS.escape(String(programIndex))}"]`)
      : null
    const active = programActive || (id
      ? this.element.querySelector(`.playlist [data-extra-id="${CSS.escape(id)}"]`)
      : null)
    if (active) {
      active.classList.add("active")
      if (programActive) active.setAttribute("aria-current", "true")
      else active.querySelector(".playlist-btn")?.setAttribute("aria-current", "true")
    }
    if (this.liveStation) this._syncLiveCatalog(t)
    if (this.liveStation) this._primeNextLiveAudio()

    // event for external listeners
    if (id) {
      document.dispatchEvent(new CustomEvent("fm:trackchange", {
        detail: { id, kind: t?.kind || "track", audio_url: audioUrl, share_url: share }
      }))
    }

    // title counter
    if (this.hasTitlePlayCountTarget) {
      this.titlePlayCountTarget.textContent =
        this._formatCount(Number(t?.plays_count || 0))
    }

    // ensure controls enabled (no ad)
    this._setSkipButtonsDisabled(this.liveStation)

    // reset listen timer anchor until 'playing' fires
    this._pauseListenTimer()
    this._refreshTrackToggleUI()
  }

  _currentExtra() {
    return this._activePlaylist()?.[this.index]
  }

  _updateVisualForTrack(t) {
    if (!this.hasVisualWrapTarget || !this.hasArtGridTarget) return

    this._removeAdPlayOverlay()

    this.visualWrapTarget.innerHTML = ""
    this.visualWrapTarget.style.display = "none"
    this.artGridTarget.classList.remove("two")
    this.artGridTarget.classList.add("one")
    this._hideTrackToggle()

    if (!t.second_asset_url) return

    if ((t.second_asset_kind || "").toLowerCase() === "video") {
      const video = document.createElement("video")
      // decorative visualizer: autoplay muted loop; no controls
      video.setAttribute("muted", "")
      video.setAttribute("autoplay", "")
      video.setAttribute("loop", "")
      video.setAttribute("playsinline", "")
      video.setAttribute("preload", "metadata")
      video.style.width = "100%"
      const src = document.createElement("source")
      src.src = t.second_asset_url
      src.type = t.second_asset_content_type || "video/mp4"
      video.appendChild(src)
      this.visualWrapTarget.appendChild(video)
      this._showTrackToggle()
      this._syncVisualVideoPlayback()
    } else {
      const img = document.createElement("img")
      img.src = t.second_asset_url
      img.alt = "track image"
      img.style.width = "100%"
      this.visualWrapTarget.appendChild(img)
      this._showTrackToggle()
    }

    this.visualWrapTarget.style.display = ""
    this.artGridTarget.classList.remove("one")
    this.artGridTarget.classList.add("two")
  }

  // ===== Ad logic =====

  _hasAds() {
    return Array.isArray(this.adsValue) && this.adsValue.length > 0
  }

  _adsEnabled() {
    return this._hasAds() && Number(this.adIntervalSecondsValue || 0) > 0
  }

  _handleNextClick() {
    if (this.liveStation) return
    if (this._isAdMode) {
      // during ad: NEXT skips ad only after lock window
      if (this._skipLocked()) return
      this._endAdAndResume()
      return
    }
    this._advance(1)
  }

  _maybeQueueAdAndNext() {
    if (this._isAdMode) return

    if (this.liveStation) {
      this._syncLiveStation({ autoplay: !this._liveUserPaused, hard: true })
      return
    }

    if (this._adPending && this._adsEnabled()) {
      this._playNextAd()
    } else {
      this._advance(1)
    }
  }

  _playNextAd() {
    const ads = this.adsValue || []
    if (!ads.length) {
      // if pre-roll but no ads, just start music
      if (this._preRoll) {
        this._preRoll = false
        this._loadCurrent(true)
      }
      return
    }

    const ad = ads[this._adIndex % ads.length]
    this._adIndex += 1

    this._currentAd = ad
    this._isAdMode = true
    this._adPending = false
    this._refreshTrackToggleUI()

    this._removeAdPlayOverlay()

    // stop music timer entirely during ads
    this._pauseListenTimer()

    // lock buttons initially
    this._setSkipButtonsDisabled(true)
    this._adLockUntil = performance.now() + (this.adSkipLockSecondsValue * 1000)
    this._scheduleAdSkipUnlock()

    // reset visuals / text
    if (this.hasLyricsTarget) this.lyricsTarget.textContent = ""
    this._setTicker("SPONSORED AD", "", "")
    if (this.hasAlbumTarget) {
      this.albumTarget.textContent = "SPONSORED"
      this.albumTarget.removeAttribute("href")
    }
    if (this.hasArtistTarget) {
      this.artistTarget.textContent = "313.CASH"
      this.artistTarget.href = "https://313.cash"
    }

    const kind = (ad.kind || this._inferAdKind(ad)).toLowerCase()

    if (kind === "video") {
      this._playVideoAd(ad)
    } else if (kind === "audio") {
      this._playAudioAd(ad)
    } else {
      this._showImageAd(ad)
    }
  }

  _inferAdKind(ad) {
    const ct = (ad.content_type || "").toLowerCase()
    if (ct.startsWith("video/")) return "video"
    if (ct.startsWith("audio/")) return "audio"
    if (ct.startsWith("image/")) return "image"
    return "image"
  }

  // helper: build click handler for ad_link
  _openAdLinkIfPresent(ad) {
    const url = (ad && ad.ad_link) ? String(ad.ad_link) : ""
    if (!url) return null

    return (event) => {
      try {
        window.open(url, "_blank", "noopener,noreferrer")
      } catch (_) {}
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault()
      }
    }
  }

  // video ad: full sound, ad owns the visualWrap
  _playVideoAd(ad) {
    if (!this.hasVisualWrapTarget || !this.hasArtGridTarget) {
      this._endAdAndResume()
      return
    }

    this._removeAdPlayOverlay()
    this._hideTrackToggle()

    const src =
      ad.video_url ||
      ad.second_asset_url ||
      ad.audio_url || // fallback
      ad.url

    if (!src) {
      this._endAdAndResume()
      return
    }

    this.visualWrapTarget.innerHTML = ""
    this.visualWrapTarget.style.display = ""
    this.artGridTarget.classList.remove("one")
    this.artGridTarget.classList.add("two")

    const video = document.createElement("video")
    video.src = src
    video.setAttribute("playsinline", "")
    video.setAttribute("preload", "metadata")
    video.autoplay = true
    video.muted = false // IMPORTANT: Android may block. We'll catch and overlay play.
    video.controls = false
    video.style.width = "100%"
    this.screenWakeLock?.bindMedia(video)
    this._adVideoEl = video

    // click → ad_link
    const clickHandler = this._openAdLinkIfPresent(ad)
    if (clickHandler) {
      video.style.cursor = "pointer"
      video.addEventListener("click", clickHandler)
    }

    video.addEventListener("ended", () => this._endAdAndResume())
    this.visualWrapTarget.appendChild(video)

    // stop any main audio src so we don't double-play
    if (this.hasAudioTarget) {
      this.audioTarget.pause()
      this.audioTarget.removeAttribute("src")
    }
    this._teardownPrimaryVideo()

    // ensure music timer is OFF during ad
    this._pauseListenTimer()

    // try autoplay; if blocked, overlay big play button
    this._safePlay(video, "ad-video")
  }

  // audio ad: plays via main audio tag; show companion second_asset if present
  _playAudioAd(ad) {
    if (!this.hasAudioTarget || !ad.audio_url) {
      this._showImageAd(ad)
      return
    }

    this._removeAdPlayOverlay()
    this._hideTrackToggle()

    const absAudio = new URL(ad.audio_url, window.location.origin).toString()
    this.audioTarget.src = absAudio
    this.audioTarget.loop = false
    this.audioTarget.muted = false // IMPORTANT: Android may block. We'll catch and overlay play.

    // ensure music timer is OFF during ad
    this._pauseListenTimer()

    // companion image/video
    this._renderAdCompanionVisual(ad)

    // try autoplay; if blocked, overlay big play button on the ad visual
    this._safePlay(this.audioTarget, "ad-audio")
  }

  // pure image ad: show and auto-advance after 10s
  _showImageAd(ad) {
    if (!this.hasVisualWrapTarget || !this.hasArtGridTarget) {
      this._endAdAndResume()
      return
    }

    this._removeAdPlayOverlay()
    this._hideTrackToggle()

    const src =
      ad.image_url ||
      ad.second_asset_url ||
      ad.audio_url || // worst-case fallback for debugging
      ad.url

    if (!src) {
      this._endAdAndResume()
      return
    }

    this.visualWrapTarget.innerHTML = ""
    this.visualWrapTarget.style.display = ""
    this.artGridTarget.classList.remove("one")
    this.artGridTarget.classList.add("two")

    const img = document.createElement("img")
    img.src = src
    img.alt = this._normalizeDisplayTitle(ad.title) || "sponsor"
    img.style.width = "100%"

    // click → ad_link
    const clickHandler = this._openAdLinkIfPresent(ad)
    if (clickHandler) {
      img.style.cursor = "pointer"
      img.addEventListener("click", clickHandler)
    }

    this.visualWrapTarget.appendChild(img)

    // auto-finish after at least 10s (or lock duration if longer)
    const holdSeconds = Math.max(this.adSkipLockSecondsValue, 10)
    const adRef = this._currentAd
    setTimeout(() => {
      if (this._isAdMode && this._currentAd === adRef) {
        this._endAdAndResume()
      }
    }, holdSeconds * 1000)

    // ensure no leftover main audio/video
    if (this.hasAudioTarget) {
      this.audioTarget.pause()
      this.audioTarget.removeAttribute("src")
    }
    this._teardownPrimaryVideo()

    // ensure music timer is OFF during ad
    this._pauseListenTimer()
  }

  _renderAdCompanionVisual(ad) {
    if (!this.hasVisualWrapTarget || !this.hasArtGridTarget) return

    this._removeAdPlayOverlay()
    this._hideTrackToggle()

    this.visualWrapTarget.innerHTML = ""
    this.visualWrapTarget.style.display = "none"
    this.artGridTarget.classList.remove("two")
    this.artGridTarget.classList.add("one")

    const secondUrl = ad.second_asset_url
    const kind = (ad.second_asset_kind || "").toLowerCase()

    if (!secondUrl) {
      const placeholder = document.createElement("div")
      placeholder.className = "ad-placeholder"
      placeholder.textContent = this._normalizeDisplayTitle(ad.title) || "advertisement"
      this.visualWrapTarget.appendChild(placeholder)

      this.visualWrapTarget.style.display = ""
      this.artGridTarget.classList.remove("one")
      this.artGridTarget.classList.add("two")
      return
    }

    const clickHandler = this._openAdLinkIfPresent(ad)

    if (kind === "video") {
      const v = document.createElement("video")
      v.src = secondUrl
      v.setAttribute("playsinline", "")
      v.setAttribute("preload", "metadata")
      v.autoplay = true
      v.muted = true  // audio from main ad; video here is visual
      v.loop = true
      v.style.width = "100%"
      if (clickHandler) {
        v.style.cursor = "pointer"
        v.addEventListener("click", clickHandler)
      }
      this.visualWrapTarget.appendChild(v)
    } else {
      const img = document.createElement("img")
      img.src = secondUrl
      img.alt = this._normalizeDisplayTitle(ad.title) || "sponsor"
      img.style.width = "100%"
      if (clickHandler) {
        img.style.cursor = "pointer"
        img.addEventListener("click", clickHandler)
      }
      this.visualWrapTarget.appendChild(img)
    }

    this.visualWrapTarget.style.display = ""
    this.artGridTarget.classList.remove("one")
    this.artGridTarget.classList.add("two")
  }

  _endAdAndResume() {
    const wasPreRoll = this._preRoll
    this._exitAdMode()
    this._removeAdPlayOverlay()

    if (wasPreRoll) {
      this._preRoll = false
      this._loadCurrent(true)
    } else {
      this._advance(1)
    }
  }

  _exitAdMode() {
    if (this._adVideoEl) {
      this.screenWakeLock?.release(this._adVideoEl)
      try { this._adVideoEl.pause() } catch (_) {}
      this._adVideoEl = null
    }
    this._isAdMode = false
    this._currentAd = null
    this._adLockUntil = 0
    this._totalPlaySeconds = 0
    this._setSkipButtonsDisabled(false)

    // keep listen timer off until music 'playing' fires again
    this._pauseListenTimer()
    this._refreshTrackToggleUI()
  }

  _skipLocked() {
    if (!this._isAdMode) return false
    return performance.now() < this._adLockUntil
  }

  _setSkipButtonsDisabled(disabled) {
    const opacity = disabled ? 0.35 : 1
    const pointer = disabled ? "none" : "auto"
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.style.opacity = opacity
      this.nextBtnTarget.style.pointerEvents = pointer
    }
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.style.opacity = opacity
      this.prevBtnTarget.style.pointerEvents = pointer
    }
  }

  _scheduleAdSkipUnlock() {
    const ms = (this.adSkipLockSecondsValue || 0) * 1000
    if (!ms) return

    setTimeout(() => {
      if (!this._isAdMode) return
      if (!this._skipLocked()) {
        this._setSkipButtonsDisabled(false)
      }
    }, ms + 50)
  }

  // ===== URL → index =====

  _syncIndexFromUrl() {
    if (this.liveStation) return false
    try {
      const wantedPath = new URL(window.location.href).pathname.replace(/\/+$/, "") || "/"
      const candidates = [
        {
          list: this._activePlaylist(),
          source: this.queueSource || "station",
          playlistId: this.queueSource === "saved" ? this._savedQueuePlaylistId : null
        },
        { list: this.stationProgram || [], source: "station", playlistId: null },
        { list: this.stationPlaylist || [], source: "station", playlistId: null },
        { list: this.savedPlaylist || [], source: "saved", playlistId: this.activePlaylistId }
      ]
      const seen = new Set()

      for (const candidate of candidates) {
        if (!candidate.list.length || seen.has(candidate.list)) continue
        seen.add(candidate.list)
        const index = candidate.list.findIndex(track => {
          const share = this._safeHttpUrl(track?.share_url)
          return share && (new URL(share)).pathname.replace(/\/+$/, "") === wantedPath
        })
        if (index < 0) continue

        this.playbackQueue = candidate.list
        this.queueSource = candidate.source
        this._savedQueuePlaylistId = candidate.source === "saved" ? candidate.playlistId : null
        this.index = index
        return true
      }
    } catch (_) {}
    return false
  }

  // ===== Mute helpers =====

  _applyInitialMutePreference() {
    const muted = this._readMutePreference()
    this._applyMute(muted, { fromUser: false })
  }

  _readMutePreference() {
    try {
      return localStorage.getItem("jbMuted") === "true"
    } catch {
      return false
    }
  }

  _applyMute(muted, { fromUser } = { fromUser: false }) {
    if (this.hasAudioTarget && this.audioTarget.muted !== muted) {
      this.audioTarget.muted = muted
    }
    if (this._primaryVideoEl) {
      this._primaryVideoEl.muted = muted
    }
    if (this.hasBedAudioTarget) {
      this.bedAudioTarget.muted = muted
    }
    if (this.hasVisualWrapTarget) {
      this.visualWrapTarget
        .querySelectorAll("video")
        .forEach(v => { v.muted = muted })
    }
    // Keep the background video always muted (design rule)
    const bg = document.getElementById("fm-bg-video")
    if (bg) bg.muted = true

    if (this.hasMuteBtnTarget) {
      const btn = this.muteBtnTarget
      const icon = btn.querySelector(".icon")
      btn.classList.toggle("is-on", muted)
      btn.setAttribute("aria-pressed", muted ? "true" : "false")
      btn.setAttribute("aria-label", muted ? "Unmute all" : "Mute all")
      if (icon) icon.textContent = muted ? "🔇" : "🔊"
    }
    if (fromUser) {
      try {
        localStorage.setItem("jbMuted", muted ? "true" : "false")
      } catch {}
    }
  }

  // ===== Background video (Thing.cover_art as video) =====

  _mountBackgroundVideoIfAny() {
    const url = this.bgVideoUrlValue
    if (!url) return
    if (document.getElementById("fm-bg-video")) return

    const v = document.createElement("video")
    v.id = "fm-bg-video"
    v.src = new URL(url, window.location.origin).toString()
    v.playsInline = true
    v.muted = true
    v.loop = true
    v.autoplay = true
    v.style.position = "fixed"
    v.style.inset = "0"
    v.style.width = "100%"
    v.style.height = "100%"
    v.style.objectFit = "cover"
    v.style.opacity = "0.25"
    v.style.pointerEvents = "none"
    v.style.zIndex = "0" // background layer
    document.body.appendChild(v)
  }

  // ===== Primary video (Extra.cover_art is video) =====

  _usePrimaryVideo(src, autoplay) {
    // clear any previous primary video
    this._teardownPrimaryVideo()

    // Render in the visual area as the main media
    if (!this.hasVisualWrapTarget || !this.hasArtGridTarget) return

    this._removeAdPlayOverlay()

    // Make sure the visual area is visible
    this.visualWrapTarget.style.display = ""
    this.artGridTarget.classList.remove("one")
    this.artGridTarget.classList.add("two")

    const v = document.createElement("video")
    v.src = new URL(src, window.location.origin).toString()
    v.playsInline = true
    v.controls = true
    v.preload = "metadata"
    v.crossOrigin = "anonymous"
    v.loop = this.repeatOne

    // Respect stored mute preference
    v.muted = this._readMutePreference()

    v.style.width = "100%"
    this.visualWrapTarget.innerHTML = ""
    this.visualWrapTarget.appendChild(v)
    this._primaryVideoEl = v
    this.screenWakeLock?.bindMedia(v)
    this._showTrackToggle()

    // Wire events mirroring audioTarget
    v.addEventListener("ended", () => {
      if (this.repeatOne) this._restartRepeatedMedia(v)
      else this._maybeQueueAdAndNext()
    })
    v.addEventListener("loadedmetadata", () => this._recomputeThresholdForMedia(v))
    v.addEventListener("timeupdate", () => {
      this._maybeReportPlayForMedia(v)
      this._updateListenTimerForMedia(v)
      this._updateProgressUI(v)
    })
    v.addEventListener("playing", () => {
      this._resumeListenTimerForMedia(v)
      this._refreshTrackToggleUI()
    })
    v.addEventListener("pause", () => {
      this._syncVisualVideoPlayback()
      this._refreshTrackToggleUI()
    })
    v.addEventListener("loadedmetadata", () => this._updateProgressUI(v))
    v.addEventListener("ended", () => {
      this._syncVisualVideoPlayback()
      this._refreshTrackToggleUI()
    })
    ;["pause","waiting","seeking","stalled","suspend","emptied"].forEach(ev =>
      v.addEventListener(ev, () => this._pauseListenTimerForMedia())
    )

    // Start (or let user start)
    if (autoplay) this._safePlay(v, "music-primary-video")
    this._updateProgressUI(v)
  }

  _teardownPrimaryVideo() {
    if (!this._primaryVideoEl) return
    this.screenWakeLock?.release(this._primaryVideoEl)
    try {
      this._primaryVideoEl.pause()
      this._primaryVideoEl.remove()
    } catch {}
    this._primaryVideoEl = null
    if (this.hasAudioTarget) this._updateProgressUI(this.audioTarget)
  }

  _recomputeThresholdForMedia(mediaEl) {
    const dur = Number(mediaEl?.duration || 0)
    const pct = dur ? dur * 0.30 : 5
    this._playThreshold = Math.max(5, Math.min(25, pct))
  }

  _maybeReportPlayForMedia(mediaEl) {
    if (this.liveStation) return
    if (this._isAdMode || this._reportedPlayForCurrent) return
    const t = Number(mediaEl?.currentTime || 0)
    if (t >= this._playThreshold) {
      this._reportedPlayForCurrent = true
      const extra = this._currentExtra()
      if (extra?.id && !extra?.is_dj_segment) this._reportPlay(extra.id)
    }
  }

  _resumeListenTimerForMedia(mediaEl) {
    if (this._isAdMode) return
    this._isListeningClockOn = true
    this._lastTimeUpdateTs = performance.now()
    this._activeListenMedia = mediaEl
  }

  _pauseListenTimerForMedia() {
    this._isListeningClockOn = false
    this._lastTimeUpdateTs = null
    this._activeListenMedia = null
  }

  _updateListenTimerForMedia(mediaEl) {
    if (!this._adsEnabled()) {
      this._lastTimeUpdateTs = null
      return
    }

    if (!mediaEl || this._isAdMode || !this._isListeningClockOn) {
      this._lastTimeUpdateTs = null
      return
    }
    const now = performance.now()
    if (this._lastTimeUpdateTs) {
      const delta = (now - this._lastTimeUpdateTs) / 1000
      if (delta > 0 && delta < 5) this._totalPlaySeconds += delta
    }
    this._lastTimeUpdateTs = now

    if (!this._adPending && this._totalPlaySeconds >= this.adIntervalSecondsValue) {
      this._adPending = true
    }
  }

  // =========================================================
  // Autoplay-safe play() wrapper + BIG ad play overlay
  // =========================================================

  _safePlay(mediaEl, context = "") {
    if (!mediaEl || typeof mediaEl.play !== "function") return
    try {
      const p = mediaEl.play()
      if (p && typeof p.catch === "function") {
        p.catch((err) => {
          console.debug(`play() blocked (${context})`, err)
          this._autoplayBlocked = true

          // If we're in ad mode, show BIG PLAY on the ad visual
          if (this._isAdMode) {
            this._showAdPlayOverlay({ hint: "tap to start the ad" })
          } else {
            this._showTapOverlay()
          }
          this._refreshTrackToggleUI()
        })
      }

      // Some Android browsers don't reject play(), but remain paused.
      // If we're in ad mode and it's still paused shortly after, show overlay.
      if (this._isAdMode) {
        setTimeout(() => {
          try {
            if (mediaEl.paused) {
              this._autoplayBlocked = true
              this._showAdPlayOverlay({ hint: "tap to start the ad" })
            }
            this._refreshTrackToggleUI()
          } catch (_) {}
        }, 350)
      }
      this._refreshTrackToggleUI()
    } catch (err) {
      console.debug(`play() threw (${context})`, err)
      this._autoplayBlocked = true
      if (this._isAdMode) this._showAdPlayOverlay({ hint: "tap to start the ad" })
      else this._showTapOverlay()
      this._refreshTrackToggleUI()
    }
  }

  _showTapOverlay() {
    if (!this.hasTapOverlayTarget) return
    this.tapOverlayTarget.classList.add("is-on")
    this.tapOverlayTarget.setAttribute("aria-hidden", "false")
  }

  _hideTapOverlay() {
    if (!this.hasTapOverlayTarget) return
    this.tapOverlayTarget.classList.remove("is-on")
    this.tapOverlayTarget.setAttribute("aria-hidden", "true")
  }

  _tryResumePlayback() {
    // Try to play whichever media is currently responsible for output
    if (this._isAdMode) {
      // ad video might be inside visualWrap
      const vwVideo = this.hasVisualWrapTarget
        ? this.visualWrapTarget.querySelector("video")
        : null

      if (vwVideo && vwVideo.paused) {
        this._safePlay(vwVideo, "resume-ad-video")
        return
      }

      // audio ad uses main audio element
      if (this.hasAudioTarget && this.audioTarget.src) {
        this._safePlay(this.audioTarget, "resume-ad-audio")
        return
      }
      return
    }

    // music
    if (this.liveStation) this._syncLiveStation({ autoplay: false, hard: true })
    if (this._primaryVideoEl) {
      this._safePlay(this._primaryVideoEl, "resume-primary-video")
      if (this.liveStation) this._resumeLiveDjBed()
      return
    }
    if (this.hasAudioTarget && this.audioTarget.src) {
      this._safePlay(this.audioTarget, "resume-music")
      if (this.liveStation) this._resumeLiveDjBed()
    }
  }

  _removeAdPlayOverlay() {
    if (!this.hasVisualWrapTarget) return
    const existing = this.visualWrapTarget.querySelector(".ad-play-overlay")
    if (existing) existing.remove()
  }

  _showAdPlayOverlay({ hint = "tap to start audio" } = {}) {
    if (!this.hasVisualWrapTarget) return

    // Only show overlay if there is something visible to overlay
    const hasStuff =
      this.visualWrapTarget.querySelector("img, video") ||
      this.visualWrapTarget.childElementCount > 0

    if (!hasStuff) return

    this._removeAdPlayOverlay()

    const overlay = document.createElement("div")
    overlay.className = "ad-play-overlay"

    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "ad-play-btn"
    btn.setAttribute("aria-label", "Play")
    btn.innerHTML = `<span class="tri" aria-hidden="true">▶</span>`

    btn.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.userStart()
    })

    const hintEl = document.createElement("div")
    hintEl.className = "ad-play-hint"
    hintEl.textContent = hint

    overlay.appendChild(btn)
    overlay.appendChild(hintEl)
    this.visualWrapTarget.appendChild(overlay)
  }

  _activeMedia() {
    if (this._primaryVideoEl) return this._primaryVideoEl
    if (this.hasAudioTarget && this.audioTarget.src) return this.audioTarget
    return null
  }

  _currentVisualVideo() {
    if (!this.hasVisualWrapTarget) return null
    return this.visualWrapTarget.querySelector("video")
  }

  _syncVisualVideoPlayback() {
    const visualVideo = this._currentVisualVideo()
    if (!visualVideo) return
    if (this._primaryVideoEl && visualVideo === this._primaryVideoEl) return

    const media = this._activeMedia()
    if (!media || this._isAdMode) return

    if (media.paused || media.ended) {
      visualVideo.pause()
      return
    }

    const playAttempt = visualVideo.play?.()
    if (playAttempt && typeof playAttempt.catch === "function") {
      playAttempt.catch(() => {})
    }
  }

  _updateProgressUI(mediaEl) {
    if (!mediaEl) return
    const duration = Number(mediaEl.duration || 0)
    const current = Number(mediaEl.currentTime || 0)

    if (this.hasSeekBarTarget) {
      if (this.liveStation) {
        this.seekBarTarget.setAttribute("aria-valuemin", "0")
        this.seekBarTarget.setAttribute("aria-valuemax", String(Number.isFinite(duration) && duration > 0 ? duration : 0))
        this.seekBarTarget.setAttribute("aria-valuenow", String(Number.isFinite(current) ? current : 0))
      } else {
        this.seekBarTarget.max = Number.isFinite(duration) && duration > 0 ? duration : 100
        this.seekBarTarget.value = Number.isFinite(current) ? current : 0
      }
    }

    if (this.hasCurrentTimeTarget) {
      this.currentTimeTarget.textContent = this._formatClock(current)
    }
    if (this.hasDurationTimeTarget) {
      this.durationTimeTarget.textContent = this._formatClock(duration)
    }

    if (this.hasWaveProgressTarget) {
      const pct = duration > 0 ? Math.max(0, Math.min(100, (current / duration) * 100)) : 0
      this.waveProgressTarget.style.width = `${pct}%`
    }
  }

  _formatClock(seconds) {
    const s = Number(seconds)
    if (!Number.isFinite(s) || s < 0) return "0:00"
    const total = Math.floor(s)
    const mins = Math.floor(total / 60)
    const secs = String(total % 60).padStart(2, "0")
    return `${mins}:${secs}`
  }

  _showTrackToggle() {
    if (!this.hasTrackToggleBtnTarget) return
    if (this._isAdMode) {
      this._hideTrackToggle()
      return
    }
    this.trackToggleBtnTarget.hidden = false
    this._refreshTrackToggleUI()
  }

  _hideTrackToggle() {
    if (!this.hasTrackToggleBtnTarget) return
    this.trackToggleBtnTarget.hidden = true
  }

  _refreshTrackToggleUI() {
    const media = this._activeMedia()
    const canToggle = Boolean(media) && !this._isAdMode
    const isPaused = !canToggle || media.paused
    const icon = isPaused ? "▶️" : "⏸️"
    const subject = this.liveStation ? "live station" : "track"
    const label = isPaused ? `Play ${subject}` : `Pause ${subject}`

    if (this.hasTrackToggleIconTarget) {
      this.trackToggleIconTarget.textContent = icon
    }
    if (this.hasTrackToggleBtnTarget) {
      this.trackToggleBtnTarget.setAttribute("aria-label", label)
    }
    if (this.hasTransportToggleIconTarget) {
      this.transportToggleIconTarget.textContent = icon
    }
    if (this.hasTransportToggleBtnTarget) {
      this.transportToggleBtnTarget.disabled = !canToggle
      this.transportToggleBtnTarget.setAttribute("aria-label", label)
    }
  }
}
